import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../../api/axios';
import useAuthStore from '../../store/authStore';
import toast from 'react-hot-toast';
import AdminMenu from '../admin/Menu';
import { hasPlanFeature } from '../../utils/planAccess';
import UpgradeModal from '../../components/UpgradeModal';
import { fmtDateTime } from '../../utils/dateFormat';
import SearchSelect from '../../components/SearchSelect';
import Layout from '../../components/Layout';
import { confirm } from '../../components/ConfirmDialog';
import LossCalcModal from '../../components/LossCalcModal';

// Mədaxil itkisi → maya əmsalı (backend utils/itki.js ilə eyni). Dəyər «alınan 1 vahiddən nə qədər itir»:
// kq/lt/ədəd 0.446 = 1 kq-dan 0.446 kq, qram/ml q/kq ilə. Real maya = qiymət ÷ (1 − itki).
const itkiFactor = (loss, unit) => {
    const f = (unit === 'gram' || unit === 'ml') ? (Number(loss) || 0) / 1000 : (Number(loss) || 0);
    return f > 0 && f < 1 ? 1 / (1 - f) : 1;
};

const getAssetBaseUrl = () => process.env.REACT_APP_API_URL?.replace('/api', '') || window.location.origin;

// Mətbəx printer açarı → görünən ad (texkarta çap filtri üçün)
const PRINTER_LABELS = {
    'xp-istimetbex': 'İsti Mətbəx',
    'xp-soyuq':      'Soyuq Mətbəx',
    'xp-manqal':     'Manqal',
    'xp-bar':        'Bar',
    'xp-cayci':      'Çayçı',
    'xp-ofisiant':   'Ofisiant',
};

const WarehouseDashboard = () => {
    const { user }                   = useAuthStore();
    const canUseWarehouse = hasPlanFeature(user?.tenant_plan, 'warehouse');
    const isAdmin = user?.role === 'admin' || user?.role === 'superadmin' || !!user?.is_manager;
    // Kassir yalnız BAXIŞ üçün girir (kassir_stock_view) — yalnız Stok idarəsi + Hərəkət tarixi,
    // heç bir əməliyyat düyməsi/sütunu yox.
    const isKassirView = user?.role === 'kassir';

    const [tab, setTab]             = useState('stock');
    const [ingredients, setIngredients] = useState([]);
    const [loading, setLoading]     = useState(true);
    const [stockSearch, setStockSearch] = useState('');
    const [stockDepotFilter, setStockDepotFilter] = useState('');

    // Stock modal
    const [stockModal, setStockModal]   = useState(false);
    const [selectedIng, setSelectedIng] = useState(null);
    const [stockForm, setStockForm]     = useState({ type: 'giris', quantity: '', note: '' });

    // Ingredient modal
    const [ingModal, setIngModal]   = useState(false);
    const [lossCalcOpen, setLossCalcOpen] = useState(false); // «İtki hesabla» kalkulyatoru
    const [editIng, setEditIng]     = useState(null);
    const [ingForm, setIngForm]     = useState({
        name: '', unit: 'eded', stock: 0, min_stock: 0, cost_per_unit: 0, depot_id: ''
    });
    const [deleteIngredient, setDeleteIngredient] = useState(null);

    // Movements
    const [movements, setMovements]     = useState([]);
    const [selectedIngId, setSelectedIngId] = useState(null);
    const [historySearch, setHistorySearch] = useState('');

    // Recipe
    const [menuItems, setMenuItems]         = useState([]);
    const [allRecipes, setAllRecipes]       = useState([]); // flat list from /recipes/all
    const [recipes, setRecipes]             = useState([]); // current right-panel recipe rows
    const [selectedItem, setSelectedItem]   = useState(null);
    const [selectedVariant, setSelectedVariant] = useState(null); // {id, name} or null = base
    const selectedRowRef    = useRef(null);   // sol siyahıda seçilmiş məhsul sətri (scroll üçün)
    const scrollToSelectedRef = useRef(false); // Menyudan gəldikdə həmin məhsula scroll et
    const [priceEdit, setPriceEdit]         = useState('');   // satış qiyməti (texkartadan redaktə)
    const [priceSaving, setPriceSaving]     = useState(false);
    const [priceEditing, setPriceEditing]   = useState(false); // ✏️ vurulanda açılır
    const [variantPickModal, setVariantPickModal] = useState(null); // menu item when picking variant
    const [itemVariants, setItemVariants]   = useState({}); // { [itemId]: variant[] }
    const [recipeForm, setRecipeForm]       = useState({ ingredient_id: '', quantity: '' });
    // Texkarta miqdar yazma vahidi: kg/litr üçün 'small' (qram/ml) default, 'base' (kq/lt) toggle
    const [recipeUnitMode, setRecipeUnitMode] = useState('small');
    // Klaviatura naviqasiyası: inqredient axtarışı → arrow/enter
    const recipeSearchRef = useRef(null);
    const recipeQtyRef = useRef(null);
    const [recipeHighlight, setRecipeHighlight] = useState(-1);
    const [recipeSearch, setRecipeSearch]   = useState('');
    const [recipeLeftSearch, setRecipeLeftSearch] = useState('');
    const [recipeLeftIngId, setRecipeLeftIngId]   = useState(''); // İnqredient rejimində combodan seçilmiş inqredient id (string)
    const [printCatName, setPrintCatName] = useState(''); // texkarta kateqoriya çapı üçün seçilmiş kateqoriya
    const [printPrinterKey, setPrintPrinterKey] = useState(''); // '' = bütün printerlər, ya da mətbəx printer açarı
    const [waModal, setWaModal] = useState(false);              // texkarta WhatsApp popup
    const [waNum, setWaNum] = useState('');                     // cari yazılan nömrə (1 input)
    const [waWithPrices, setWaWithPrices] = useState(false);    // alış qiymətləri ilə göndər
    const [waHistory, setWaHistory] = useState(() => {          // son göndərilmiş nömrələr (serverdən; localStorage köhnə fallback)
        try { return JSON.parse(localStorage.getItem('wa_recent_nums') || '[]'); } catch { return []; }
    });
    const [waSending, setWaSending] = useState(false);
    // WhatsApp mesaj rejimi: 'item' = seçilmiş məhsulun texkartası; 'ingredient' = seçilmiş inqrediyentin hansı məhsullarda nə qədər olduğu
    const [waMode, setWaMode] = useState('item');
    const [waIng, setWaIng]   = useState(null); // { id, name } — ingredient rejimi üçün seçilmiş inqredient
    const [recipeLeftSearchMode, setRecipeLeftSearchMode] = useState('item'); // 'item' | 'ingredient'
    const [recipeMidSearch, setRecipeMidSearch]   = useState('');
    // Snapshot of itemsWithRecipe used for sort order — frozen while editing a single item
    const [recipeSortSnapshot, setRecipeSortSnapshot] = useState(null);
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);

    // ── Sayım state ────────────────────────────────────────────
    const [sayimData,      setSayimData]      = useState([]);
    const [sayimLoading,   setSayimLoading]   = useState(false);
    const [sayimSearch,    setSayimSearch]    = useState('');
    const [sayimEditId,    setSayimEditId]    = useState(null);
    const [sayimEditForm,  setSayimEditForm]  = useState({ amount: '', sayim_date: '', note: '' });
    const [tergModal,      setTergModal]      = useState(null);   // {id, name, unit}
    const [tergForm,       setTergForm]       = useState({ amount: '', note: '' });
    const [medItkiModal,   setMedItkiModal]   = useState(null);   // {id, name, unit}
    const [medItkiForm,    setMedItkiForm]    = useState({ amount: '', note: '' });
    const [prefModal,      setPrefModal]      = useState(false);
    const [prefForm,       setPrefForm]       = useState({ menu_item_id: '', variant_id: '', quantity: '', note: '' });
    const [prefVariants,   setPrefVariants]   = useState([]);
    const [sayimSessions,  setSayimSessions]  = useState([]);   // paralel anbar sessiyaları
    const [sayimSelDepot,  setSayimSelDepot]  = useState(null); // Yeni Sayım/Bitir üçün seçilmiş aktiv anbar (0=hamısı)
    const [bitirModal,     setBitirModal]     = useState(false);
    const [recipeModal,    setRecipeModal]    = useState(false); // texkarta çap modal
    const [bitirNote,      setBitirNote]      = useState('');
    const [bitirLoading,   setBitirLoading]   = useState(false);
    const [historyModal,   setHistoryModal]   = useState(false);
    const [historyData,    setHistoryData]    = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [allDateModal,   setAllDateModal]   = useState(false);
    const [allDateForm,    setAllDateForm]    = useState({ sayim_date: '', note: '' });
    const [allDateLoading, setAllDateLoading] = useState(false);

    // ── Yeni Sayım ────────────────────────────────────────────
    const [yeniSayimModal,  setYeniSayimModal]  = useState(false);
    const [yeniSayimInputs,    setYeniSayimInputs]    = useState({});
    const [yeniSayimResult,    setYeniSayimResult]    = useState(null);
    const [yeniSayimSearch,    setYeniSayimSearch]    = useState('');
    const [yeniSayimBoşOnly,   setYeniSayimBoşOnly]   = useState(false);

    // ── Depolar ───────────────────────────────────────────────
    const [depots,      setDepots]      = useState([]);
    const [depotModal,  setDepotModal]  = useState(false);
    const [editDepot,   setEditDepot]   = useState(null);
    const [depotForm,   setDepotForm]   = useState({ name: '' });
    const [depotLoading, setDepotLoading] = useState(false);

    // ── Printer seçim (sayım çapı) ────────────────────────────
    const [printers,       setPrinters]       = useState([]);
    const [printSelectModal, setPrintSelectModal] = useState(null); // null | 'sayim' | 'yeniSayim'
    const [selectedPrinter,  setSelectedPrinter]  = useState('');
    const [pendingPrintData, setPendingPrintData]  = useState(null);

    useEffect(() => {
        if (!canUseWarehouse) {
            setLoading(false);
            return;
        }
        loadIngredients();
        loadDepots();
        if (tab === 'recipe' || tab === 'sayim') loadRecipeTab();
        if (tab === 'sayim' || tab === 'stock') loadSayim();
        // Son göndərilən WhatsApp nömrələri — tenant üzrə (serverdən, bütün cihazlarda ortaq)
        api.get('/recipes/wa-recent').then((r) => { if (Array.isArray(r.data?.numbers)) setWaHistory(r.data.numbers); }).catch(() => {});
    }, [tab, canUseWarehouse]);

    const navigate = useNavigate();
    const location = useLocation();
    // Menyudan gəldikdə "Geri" düyməsi üçün item id (yalnız bu halda görünür)
    const [menuReturnItemId, setMenuReturnItemId] = useState(null);
    // Deep-link: Menyudan "📋 Texkarta" → ?tab=recipe&item=<id>.
    // URL dəyişməsinə REAKTİV: Dashboard onsuz da mount olubsa (məs. daxili "Menyu idarəsi"
    // tabından gəlirsə — eyni route) köhnə mount-only effekt işləmirdi. İndi hər URL dəyişməsində
    // VƏ menuItems yüklənəndə işləyir. menuItems hələ yüklənməyibsə URL saxlanır, yükləndikdə seçilir.
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get('tab') !== 'recipe') return;
        setTab('recipe');
        const it = params.get('item');
        if (!it) { navigate('/warehouse/dashboard', { replace: true }); return; }
        setMenuReturnItemId(Number(it));
        if (!menuItems.length) return; // məhsullar hələ yüklənməyib — yükləndikdə təkrar işləyəcək
        const item = menuItems.find((m) => Number(m.id) === Number(it));
        if (item) {
            setSelectedItem(item);
            setSelectedVariant(null);
            loadRecipes(item.id, null);
            setRecipeLeftSearchMode('item'); // məhsul rejimi (siyahı görünsün)
            setRecipeLeftSearch('');         // filtr sıfırla ki, məhsul siyahıda olsun
            scrollToSelectedRef.current = true; // sol siyahıda həmin məhsula scroll et
        }
        // URL-i təmizlə (tapılsa da, tapılmasa da — təkrar işləməsin)
        navigate('/warehouse/dashboard', { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search, menuItems]);

    // Menyudan gəldikdə: sol «Məhsul seç» siyahısını seçilmiş məhsula sürüşdür + mərkəzə gətir
    useEffect(() => {
        if (!scrollToSelectedRef.current || !selectedItem) return;
        const t = setTimeout(() => {
            try { selectedRowRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch { /* ignore */ }
            scrollToSelectedRef.current = false;
        }, 80);
        return () => clearTimeout(t);
    }, [selectedItem]);

   const loadIngredients = async () => {
    try {
        const res = await api.get('/ingredients/all');
        setIngredients(res.data.data);
    } catch (err) {
        console.error('Ingredients error:', err.response?.data);
        toast.error(err.response?.data?.msg || 'Anbar yüklənmədi.');
    } finally {
        setLoading(false);
    }
};
    const loadRecipeTab = async () => {
        try {
            const [itemRes, recipeRes] = await Promise.all([
                api.get('/menu/items/all'),
                api.get('/recipes/all').catch(() => ({ data: { data: [] } }))
            ]);
            setMenuItems(itemRes.data.data || []);
            setAllRecipes(recipeRes.data.data || []);
        } catch {
            toast.error('Menyu yüklənmədi.');
        }
    };

    const loadRecipes = async (itemId, variantId = null) => {
        try {
            let res;
            if (variantId) {
                res = await api.get(`/recipes/variant/${variantId}`);
            } else {
                res = await api.get(`/recipes/${itemId}`);
            }
            setRecipes(res.data.data || []);
        } catch {
            toast.error('Texkarta yüklənmədi.');
        }
    };

    const loadMovements = async (ingId) => {
        try {
            const res = await api.get(`/ingredients/${ingId}/movements`);
            setMovements(res.data.data);
            setSelectedIngId(ingId);
        } catch {
            toast.error('Hərəkətlər yüklənmədi.');
        }
    };

    // ── SAYIM ────────────────────────────────────────────────
    const loadSayim = async () => {
        setSayimLoading(true);
        try {
            const res = await api.get('/sayim/report');
            setSayimData(res.data.data || []);
            try {
                const ses = await api.get('/sayim/session');
                const list = ses.data?.sessions || [];
                setSayimSessions(list);
                // Yeni Sayım/Bitir üçün aktiv anbar seçimi: mövcud seçim aktivdirsə saxla, yoxsa ilk aktivi seç
                const act = list.filter(s => s.status === 'active');
                setSayimSelDepot(prev => {
                    if (prev !== null && act.some(s => Number(s.depot_id) === Number(prev))) return prev;
                    return act.length ? Number(act[0].depot_id) : null;
                });
            } catch { /* ignore */ }
        } catch { toast.error('Sayım yüklənmədi.'); }
        finally { setSayimLoading(false); }
    };

    // Seçilmiş aktiv anbarın sessiyası (Yeni Sayım/Bitir bununla işləyir)
    const sayimSelNum = sayimSelDepot === null ? null : Number(sayimSelDepot);
    const sayimSession = sayimSessions.find(s => Number(s.depot_id) === sayimSelNum)
        || { status: 'idle', depot_id: sayimSelNum, depot_name: null };
    const sayimRequested = sayimSessions.filter(s => s.status === 'requested');
    const sayimActiveSessions = sayimSessions.filter(s => s.status === 'active');
    const depLabelOf = (s) => Number(s.depot_id) === 0 ? 'Bütün depolar' : (s.depot_name || `Depo ${s.depot_id}`);

    const approveSayim = async (depotId) => {
        try { await api.post('/sayim/session/approve', { depot_id: Number(depotId) || 0 }); toast.success('Kassir sayımı aktivləşdirildi.'); loadSayim(); }
        catch (e) { toast.error(e.response?.data?.msg || 'Xəta.'); }
    };
    const rejectSayim = async (depotId) => {
        if (!(await confirm('Kassir sayım istəyi imtina edilsin?'))) return;
        try { await api.post('/sayim/session/reject', { depot_id: Number(depotId) || 0 }); toast.success('İmtina edildi.'); loadSayim(); }
        catch (e) { toast.error(e.response?.data?.msg || 'Xəta.'); }
    };
    // Admin özü sayım başladır (kassir olmadan) — bütün depoları aktiv edir.
    // Bundan sonra "Sayımı Bitir" işə düşür (təsadüfi bitirmənin qarşısı alınır).
    const startAdminSayim = async () => {
        if (!(await confirm('Yeni sayım başladılsın? (bütün depolar üçün — sonra "Sayımı Bitir" aktiv olacaq)'))) return;
        try { await api.post('/sayim/session/approve', { depot_id: 0 }); toast.success('Sayım başladıldı. İndi saya bilərsiniz.'); loadSayim(); }
        catch (e) { toast.error(e.response?.data?.msg || 'Xəta.'); }
    };

    const openSayimEdit = (row) => {
        setSayimEditId(row.id);
        const dt = row.sayim_date ? row.sayim_date.slice(0, 16) : new Date().toISOString().slice(0, 16);
        setSayimEditForm({ amount: row.kecan_sayim, sayim_date: dt, note: '' });
    };

    const handleSaveSayimEdit = async (ingId) => {
        try {
            await api.put(`/sayim/snapshot/${ingId}`, sayimEditForm);
            toast.success('Keçən sayım yeniləndi.');
            setSayimEditId(null);
            loadSayim();
        } catch (err) { toast.error(err.response?.data?.msg || 'Xəta baş verdi.'); }
    };


    const handleAddTerg = async (e) => {
        e.preventDefault();
        try {
            await api.post('/sayim/terg', { ingredient_id: tergModal.id, ...tergForm });
            toast.success('Tərg qeyd edildi.');
            setTergModal(null); setTergForm({ amount: '', note: '' });
            loadSayim();
        } catch (err) { toast.error(err.response?.data?.msg || 'Xəta baş verdi.'); }
    };

    const handleAddMedItki = async (e) => {
        e.preventDefault();
        try {
            await api.post('/sayim/medaxil-itki', { ingredient_id: medItkiModal.id, ...medItkiForm });
            toast.success('Mədaxil itkisi qeyd edildi.');
            setMedItkiModal(null); setMedItkiForm({ amount: '', note: '' });
            loadSayim();
        } catch (err) { toast.error(err.response?.data?.msg || 'Xəta baş verdi.'); }
    };

    const handleAddPref = async (e) => {
        e.preventDefault();
        try {
            await api.post('/sayim/prefaktoriya', prefForm);
            toast.success('Prefaktoriya qeyd edildi.');
            setPrefModal(false); setPrefForm({ menu_item_id: '', variant_id: '', quantity: '', note: '' }); setPrefVariants([]);
            loadSayim();
        } catch (err) { toast.error(err.response?.data?.msg || 'Xəta baş verdi.'); }
    };

    const handleBitir = async () => {
        setBitirLoading(true);
        try {
            // Use React state first (persists after modal close), fallback to localStorage
            let inputs = { ...yeniSayimInputs };
            if (!Object.keys(inputs).length) {
                try {
                    const saved = localStorage.getItem(SAYIM_STORAGE_KEY);
                    if (saved) inputs = JSON.parse(saved);
                } catch {}
            }
            // Kassir aktiv sayımdadırsa: boş buraxılanlar (aktiv anbar üzrə) 0 kimi keçir
            // (hesablanmış qalıq deyil) — kassir gəzib saymayıbsa 0-dır.
            if (sayimSession.status === 'active') {
                const depId = sayimSession.depot_id ? Number(sayimSession.depot_id) : null;
                const scope = depId ? sayimData.filter(r => r.depot_id === depId) : sayimData;
                for (const r of scope) {
                    if (inputs[r.id] === undefined || inputs[r.id] === '') inputs[String(r.id)] = 0;
                }
            }
            const bitirRes = await api.post('/sayim/bitir', { inputs, note: bitirNote, depot_id: Number(sayimSelDepot) || 0 });
            toast.success(bitirRes.data?.msg || 'Sayım bitirildi. Yeni keçən sayım qeydə alındı.');
            clearYeniSayimStorage();
            setYeniSayimInputs({});
            setYeniSayimResult(null);
            setBitirModal(false); setBitirNote('');
            loadSayim();
        } catch (err) { toast.error(err.response?.data?.msg || 'Xəta baş verdi.'); }
        finally { setBitirLoading(false); }
    };

    const loadHistory = async () => {
        setHistoryLoading(true);
        try {
            const res = await api.get('/sayim/history');
            setHistoryData(res.data.data || []);
        } catch { }
        finally { setHistoryLoading(false); }
    };

    // Tarixçə qeydində kassirin əl ilə yazdığı neçə sayım var (from_input=true)
    const kassirCountOf = (h) => {
        try {
            const snap = typeof h.snapshot === 'string' ? JSON.parse(h.snapshot) : h.snapshot;
            if (!Array.isArray(snap)) return 0;
            return snap.filter(r => r && r.from_input === true).length;
        } catch { return 0; }
    };

    const handleRestore = async (histId) => {
        if (!(await confirm('Bu sayım ləğv olunub əvvəlki keçən sayıma qayıdacaq. Kassirin bu sayımda yazdığı dəyərlər varsa, "Sayılan"a geri gələcək və sayım yenidən aktiv olacaq. Davam edilsin?'))) return;
        try {
            const res = await api.post(`/sayim/restore/${histId}`);
            toast.success(res.data?.msg || 'Bərpa edildi.');
            setHistoryModal(false);
            loadSayim();
        } catch (err) { toast.error(err.response?.data?.msg || 'Xəta baş verdi.'); }
    };

    const handleSetAllDate = async (e) => {
        e.preventDefault();
        setAllDateLoading(true);
        try {
            await api.put('/sayim/snapshot-all', allDateForm);
            toast.success('Bütün inqredientlər üçün sayım tarixi tətbiq edildi.');
            setAllDateModal(false);
            loadSayim();
        } catch (err) { toast.error(err.response?.data?.msg || 'Xəta baş verdi.'); }
        finally { setAllDateLoading(false); }
    };

    // ── DEPOLAR ──────────────────────────────────────────────
    const loadDepots = async () => {
        try {
            const res = await api.get('/depots');
            setDepots(res.data.data || []);
        } catch {}
    };

    const loadPrinters = async () => {
        try {
            const res = await api.get('/printers');
            const list = res.data.data || [];
            setPrinters(list);
            return list;
        } catch { return []; }
    };

    const handleDepotSubmit = async (e) => {
        e.preventDefault();
        setDepotLoading(true);
        try {
            if (editDepot) {
                await api.put(`/depots/${editDepot.id}`, depotForm);
                toast.success('Depo yeniləndi.');
            } else {
                await api.post('/depots', depotForm);
                toast.success('Depo əlavə edildi.');
            }
            setDepotModal(false);
            setEditDepot(null);
            setDepotForm({ name: '' });
            loadDepots();
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Xəta baş verdi.');
        } finally {
            setDepotLoading(false);
        }
    };

    const handleDepotDelete = async (depot) => {
        if (!(await confirm(`"${depot.name}" silinsin?`))) return;
        try {
            await api.delete(`/depots/${depot.id}`);
            toast.success('Silindi.');
            loadDepots();
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Silinə bilmədi.');
        }
    };

    // ── YENİ SAYIM ───────────────────────────────────────────
    const SAYIM_STORAGE_KEY = `yeniSayim_${user?.tenant_id}`;

    const openYeniSayim = async () => {
        // Əvvəlcə kassirin serverdə saxladığı "Sayılan" dəyərlərini götür
        let inputs = {};
        try {
            const d = await api.get('/sayim/draft');
            const map = d.data?.data || {};
            for (const [k, v] of Object.entries(map)) inputs[k] = String(v);
        } catch { /* ignore */ }
        // Kassir qaralaması boşdursa, lokal (admin) qaralamaya keç
        if (Object.keys(inputs).length === 0) {
            try {
                const saved = localStorage.getItem(SAYIM_STORAGE_KEY);
                if (saved) inputs = JSON.parse(saved);
            } catch { /* ignore */ }
        }
        setYeniSayimInputs(inputs);
        setYeniSayimResult(null);
        setYeniSayimSearch('');
        setYeniSayimModal(true);
    };

    const saveYeniSayimToStorage = (inputs) => {
        try { localStorage.setItem(SAYIM_STORAGE_KEY, JSON.stringify(inputs)); } catch {}
    };

    // Admin "Yeni Sayım"-da dəyişəndə kassirin gördüyü server qaralamasına sinxronla
    // (yalnız aktiv sessiyada; debounce 700ms ki, hər hərfdə yox)
    const draftSyncTimer = useRef(null);
    const syncDraftToServer = (inputs) => {
        if (sayimSession.status !== 'active') return;
        const dep = Number(sayimSelDepot) || 0;
        if (draftSyncTimer.current) clearTimeout(draftSyncTimer.current);
        draftSyncTimer.current = setTimeout(async () => {
            try {
                const items = Object.entries(inputs).map(([ingredient_id, amount]) => ({ ingredient_id, amount }));
                await api.put('/sayim/draft', { depot_id: dep, items });
            } catch { /* ignore */ }
        }, 700);
    };

    const clearYeniSayimStorage = () => {
        try { localStorage.removeItem(SAYIM_STORAGE_KEY); } catch {}
    };

    // Aktiv sessiya bir anbara bağlıdırsa Yeni Sayım yalnız o anbarı əhatə etsin
    const activeSayimDepotId = sayimSession.depot_id ? Number(sayimSession.depot_id) : null;
    const yeniSayimData = activeSayimDepotId ? sayimData.filter(r => r.depot_id === activeSayimDepotId) : sayimData;
    // Kassir AKTİV sayımdadırsa: boş buraxılan = 0 sayılır (anbarı gəzib saymayıbsa 0-dır)
    const sayimActive = sayimSession.status === 'active';

    const handleYeniSayimTamamla = () => {
        const result = yeniSayimData.map(row => {
            const raw        = yeniSayimInputs[row.id];
            const filled     = raw !== undefined && raw !== '';
            // aktiv sayımda boş = 0
            const hasSayilan = filled || sayimActive;
            const sayilan    = filled ? Number(raw) : (sayimActive ? 0 : null);
            const gozlenilen = Number(row.qaliq || 0);
            const ferq       = hasSayilan ? sayilan - gozlenilen : null;
            return { ...row, sayilan, gozlenilen, ferq, hasSayilan };
        });
        setYeniSayimResult(result);
    };

    const doPrintHtml = (html, printerName) => {
        // Silent print via C# WebView2 bridge if available
        if (window.chrome?.webview?.postMessage && printerName) {
            window.chrome.webview.postMessage({
                type: 'print-a4',
                printer: printerName,
                html,
            });
            return;
        }
        // Fallback: browser print dialog
        const win = window.open('', '_blank');
        win.document.write(html);
        win.document.close();
        win.focus();
        setTimeout(() => win.print(), 400);
    };

    // Seçilmiş kateqoriyanın məhsullarını texkartası ilə A4 (landscape) çap et.
    // printerKey verilibsə yalnız həmin mətbəx printerinə gedən məhsullar (boşdursa hamısı).
    const printCategoryRecipes = async (catName, printerKey = '') => {
        if (!catName) { toast.error('Əvvəlcə kateqoriya seçin.'); return; }
        const list = await loadPrinters();
        const a4List = list.filter(p => p.printer_type === 'a4' && p.active);
        if (!a4List.length) { toast.error('A4 printer təyin olunmayıb. Admin → Printerlər səhifəsində A4 printer əlavə edin.'); return; }

        const items = menuItems.filter(i =>
            i.active !== false && i.cat_name === catName &&
            (!printerKey || (i.printer || '') === printerKey)
        );
        if (!items.length) { toast.error('Bu seçimə uyğun məhsul yoxdur.'); return; }
        const printerLabel = printerKey ? (PRINTER_LABELS[printerKey] || printerKey) : '';

        const ingUnit = {};
        ingredients.forEach(i => { ingUnit[i.id] = i.unit; });
        const recipeMap = {};
        allRecipes.forEach(r => {
            if (!recipeMap[r.menu_item_id]) recipeMap[r.menu_item_id] = [];
            recipeMap[r.menu_item_id].push({ name: r.ingredient_name, qty: Number(r.quantity) || 0, unit: ingUnit[r.ingredient_id] || r.unit || 'kg' });
        });
        const fmtQty = (q, unit) => {
            q = Number(q) || 0;
            if (unit === 'eded') return `${q} əd`;
            if (unit === 'kg')   return q < 1 ? `${Math.round(q * 1000)} q` : `${q} kq`;
            if (unit === 'litr' || unit === 'lt') return q < 1 ? `${Math.round(q * 1000)} ml` : `${q} lt`;
            if (unit === 'gram') return `${q} q`;
            if (unit === 'ml')   return `${q} ml`;
            return `${q} ${unit}`;
        };

        let bodyRows = '';
        items.forEach((item, idx) => {
            const recipe = recipeMap[item.id] || [];
            const recipeStr = recipe.length
                ? recipe.map(r => `${r.name} ${fmtQty(r.qty, r.unit)}`).join(' ; ')
                : '<em style="color:#999">(texkarta yoxdur)</em>';
            bodyRows += `<tr><td class="num">${idx + 1}</td><td class="menu">${item.name}</td><td class="recipe">${recipeStr}</td></tr>`;
        });

        const css = `
            @page { size: A4 landscape; margin: 12mm; }
            body { font-family: 'Times New Roman', serif; font-size: 11pt; color:#000; }
            h1 { font-size: 16pt; margin:0 0 3pt; text-align:center; }
            .meta { text-align:center; font-size:9pt; color:#555; margin-bottom:12pt; }
            table { width:100%; border-collapse:collapse; }
            th,td { border:1pt solid #000; padding:4pt 6pt; vertical-align:top; }
            th { background:#e8e8e8; font-size:10pt; text-align:left; }
            td.menu { width:22%; font-weight:bold; }
            td.recipe { font-size:10pt; }
            td.num { width:4%; text-align:center; }
            tr:nth-child(even) td { background:#f7f7f7; }
            @media print { th, tr:nth-child(even) td { -webkit-print-color-adjust:exact; print-color-adjust:exact; } }
        `;
        const dateStr = new Date().toLocaleDateString('az');
        const heading = printerLabel ? `${catName} — ${printerLabel}` : catName;
        const html = `<!doctype html><html><head><meta charset="utf-8"><title>Texkarta — ${heading}</title><style>${css}</style></head><body>
            <h1>Texkarta — ${heading}</h1>
            <div class="meta">${dateStr} — ${items.length} məhsul${printerLabel ? ` (${printerLabel} printerinə gedənlər)` : ''}</div>
            <table><thead><tr><th class="num">#</th><th class="menu">Məhsul</th><th>İnqredientlər (texkarta)</th></tr></thead>
            <tbody>${bodyRows}</tbody></table>
        </body></html>`;

        doPrintHtml(html, a4List[0].name);
        toast.success(`"${heading}" texkartası ${a4List[0].name}-ə göndərildi.`);
    };

    // Texkarta WhatsApp mesajı: miqdar (1kg-dan azsa qram/ml) + inqredient adı, + maya + satış
    const waQtyFmt = (qty, unit) => {
        const q = Number(qty) || 0;
        if (unit === 'eded') return `${trimNumber(q)}əd`;
        if (unit === 'kg')   return q < 1 ? `${Math.round(q * 1000)}qram` : `${trimNumber(q)}kq`;
        if (unit === 'gram') return q < 1000 ? `${trimNumber(q)}qram` : `${trimNumber(q / 1000)}kq`;
        if (unit === 'litr') return q < 1 ? `${Math.round(q * 1000)}ml` : `${trimNumber(q)}lt`;
        if (unit === 'ml')   return q < 1000 ? `${trimNumber(q)}ml` : `${trimNumber(q / 1000)}lt`;
        return `${trimNumber(q)} ${unit}`;
    };

    // "1 vahid" etiketi (alış qiyməti üçün) — inqrediyentin öz vahidinə görə
    const oneUnitLabel = (unit) => ({ eded: '1 ədəd', kg: '1 kq', gram: '1 qram', litr: '1 lt', ml: '1 ml' }[unit] || `1 ${unit}`);

    const buildTexkartaMessage = () => {
        if (!selectedItem) return '';
        const title = `${selectedItem.name}${selectedVariant ? ` — ${selectedVariant.name}` : ''}`;
        const lines = recipes.map(r => {
            const ing = ingredients.find(i => i.id === r.ingredient_id);
            const unit = ing?.unit || r.unit || 'kg';
            if (waWithPrices) {
                // Alış qiyməti ilə: ad — 1 vahid qiymət · resept miqdarı = sətir cəmi (cədvəldəki kimi)
                const cost = Number(r.cost_per_unit || 0);
                const qty  = Number(r.quantity || 0);
                const line = cost * qty;
                let s = `${r.ingredient_name}: ${oneUnitLabel(unit)} ${cost.toFixed(2)} ₼ · ${waQtyFmt(qty, unit)} = ${line.toFixed(2)} ₼`;
                // Mədaxil itkisi varsa → itkili real maya da göstər (məs. itki ilə 11.30 ₼ = 19.21 ₼)
                const loss = Number(r.medaxil_itki_percent || 0);
                if (loss > 0) {
                    const effUnit = cost * itkiFactor(loss, unit);
                    s += ` (itki ilə ${effUnit.toFixed(2)} ₼ = ${(effUnit * qty).toFixed(2)} ₼)`;
                }
                return s;
            }
            return `${waQtyFmt(r.quantity, unit)} ${r.ingredient_name}`;
        });
        // Maya = itki nəzərə alınmaqla (eff_cost_per_unit = qiymət ÷ (1 − itki))
        const maya  = recipes.reduce((s, r) => s + Number(r.quantity || 0) * Number(r.eff_cost_per_unit ?? r.cost_per_unit ?? 0), 0);
        const hasLoss = recipes.some(r => Number(r.medaxil_itki_percent || 0) > 0);
        const price = selectedVariant ? Number(selectedVariant.price || 0) : Number(selectedItem.price || 0);
        let msg = `🍽️ ${title}\n\n${lines.join('\n')}`;
        msg += `\n\nMaya dəyəri${hasLoss ? ' (itki ilə)' : ''}: ${maya.toFixed(2)} azn`;
        if (price > 0) msg += `\nSatış qiyməti: ${price.toFixed(2)} azn`;
        return msg;
    };

    // Satış qiymətini seçilən məhsul/variant dəyişəndə redaktə sahəsinə yüklə
    useEffect(() => {
        setPriceEditing(false);
        if (!selectedItem) { setPriceEdit(''); return; }
        const p = selectedVariant ? Number(selectedVariant.price || 0) : Number(selectedItem.price || 0);
        setPriceEdit(p > 0 ? String(p) : '');
    }, [selectedItem, selectedVariant]);

    // Texkarta ekranından satış qiymətini yaz (menyuya qayıtmadan)
    const saveSalePrice = async () => {
        if (!selectedItem) return;
        const p = Number(String(priceEdit).replace(',', '.'));
        if (!Number.isFinite(p) || p < 0) { toast.error('Qiymət düzgün deyil.'); return; }
        setPriceSaving(true);
        try {
            if (selectedVariant) {
                await api.put(`/menu/variants/${selectedVariant.id}`, { price: p });
                setSelectedVariant(v => (v ? { ...v, price: p } : v));
                setItemVariants(prev => ({
                    ...prev,
                    [selectedItem.id]: (prev[selectedItem.id] || []).map(v => v.id === selectedVariant.id ? { ...v, price: p } : v),
                }));
            } else {
                await api.put(`/menu/items/${selectedItem.id}/price`, { price: p });
                setSelectedItem(it => (it ? { ...it, price: p } : it));
            }
            toast.success('Satış qiyməti yeniləndi.');
            setPriceEditing(false);
        } catch (e) {
            toast.error(e.response?.data?.msg || 'Yenilənmədi.');
        } finally { setPriceSaving(false); }
    };

    // İnqrediyent rejimi: seçilmiş inqrediyentin hansı məhsulların texkartasında nə qədər olduğu
    // Başlıqda 1 dəfə inqredient adı, altında hər məhsul → miqdar.
    const buildIngredientUsageMessage = () => {
        if (!waIng) return '';
        const byItem = new Map(); // menu_item_id → { name, qty, unit, isBase }
        for (const r of allRecipes) {
            if (r.ingredient_id !== waIng.id) continue;
            const mi = menuItems.find(m => m.id === r.menu_item_id);
            if (!mi) continue;
            const isBase = !r.variant_id;
            const prev = byItem.get(r.menu_item_id);
            // Eyni məhsulda variant sətirləri varsa əsas (variant_id boş) resepti üstün tut
            if (!prev || (isBase && !prev.isBase)) {
                byItem.set(r.menu_item_id, { name: mi.name, qty: Number(r.quantity || 0), unit: r.unit, isBase });
            }
        }
        const rows = [...byItem.values()].sort((a, b) => a.name.localeCompare(b.name, 'az'));
        const sep = '------------';
        if (!rows.length) return `${waIng.name}\n${sep}\n(Heç bir məhsulda istifadə olunmur)`;
        const lines = rows.map(r => `${r.name} - ${waQtyFmt(r.qty, r.unit)}`);
        return `${waIng.name}\n${sep}\n${lines.join('\n')}`;
    };

    const sendTexkartaWhatsapp = async () => {
        const num = String(waNum).trim();
        if (!num) { toast.error('Nömrə yazın.'); return; }
        const ingredientMode = waMode === 'ingredient';
        const message = ingredientMode ? buildIngredientUsageMessage() : buildTexkartaMessage();
        if (!message) { toast.error(ingredientMode ? 'İnqredient seçilməyib.' : 'Texkarta boşdur.'); return; }
        // Menyu şəkli varsa şəkillə göndər (caption = texkarta mesajı) — inqredient rejimində şəkil yox
        const imageUrl = (!ingredientMode && selectedItem?.image_url) ? `${getAssetBaseUrl()}${selectedItem.image_url}` : '';
        setWaSending(true);
        try {
            const res = await api.post('/recipes/whatsapp-send', { numbers: [num], message, imageUrl });
            if (res.data?.warn) toast(res.data.msg, { icon: '⚠️', duration: 9000 });
            else toast.success(res.data.msg || 'WhatsApp-a göndərildi.');
            // Tenant üzrə server siyahısı (bütün cihazlarda ortaq)
            if (Array.isArray(res.data?.recent)) setWaHistory(res.data.recent);
            setWaModal(false);
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Göndərilmədi.');
        } finally {
            setWaSending(false);
        }
    };

    const openPrintSelect = async (resultData) => {
        setPendingPrintData(resultData);
        const list = await loadPrinters();
        // Sayım/texkart A4 formatdadır — yalnız A4 printerləri göstər
        const a4List = list.filter(p => p.printer_type === 'a4' && p.active);
        if (a4List.length === 0) {
            toast.error('A4 printer təyin olunmayıb. Admin → Printerlər səhifəsində A4 printer əlavə edin.');
            return;
        }
        if (a4List.length === 1) {
            // Auto-select single A4 printer — print directly
            handleSayimPrint(resultData, a4List[0].name);
            return;
        }
        setSelectedPrinter('');
        setPrinters(a4List);   // modal-da yalnız A4 göstərilsin
        setPrintSelectModal(true);
    };

    // ── TEXKARTA ÇAPI (printer üzrə ayrı çap → C# silent print) ──
    const handleRecipePrint = async (mode) => {
        // mode: 'empty' | 'full'
        const now = new Date();
        const dateStr = now.toLocaleDateString('az-AZ', { day: '2-digit', month: 'long', year: 'numeric' });

        // Inqredient adı → unit map (yalnız 'full' mode üçün)
        const ingUnit = {};
        ingredients.forEach(i => { ingUnit[i.id] = i.unit; });

        // Menyu məhsulları → PRINTER üzrə qruplaşdırılır (içində kateqoriya alt-qrup)
        const byPrinter = {};
        menuItems.forEach(item => {
            if (!item.active) return;
            const printer = item.printer || 'Təyin edilməyib';
            if (!byPrinter[printer]) byPrinter[printer] = {};
            const cat = item.category_name || 'Digər';
            if (!byPrinter[printer][cat]) byPrinter[printer][cat] = [];
            byPrinter[printer][cat].push(item);
        });

        // Printer adı → display ad
        const PRINTER_NAMES = {
            'xp-istimetbex': 'İsti Mətbəx',
            'xp-soyuq':      'Soyuq Mətbəx',
            'xp-manqal':     'Manqal',
            'xp-bar':        'Bar',
            'xp-cayci':      'Çayçı',
            'xp-ofisiant':   'Ofisiant',
        };

        // Hər menyu məhsulu üçün resept sətirləri
        const recipeMap = {}; // menu_item_id → [{ingredient_name, quantity, unit}]
        allRecipes.forEach(r => {
            if (!recipeMap[r.menu_item_id]) recipeMap[r.menu_item_id] = [];
            const u = ingUnit[r.ingredient_id] || r.unit || 'kg';
            recipeMap[r.menu_item_id].push({ name: r.ingredient_name, qty: Number(r.quantity) || 0, unit: u });
        });

        // Miqdar formatla (kq→qram, lt→ml)
        const fmtQty = (qty, unit) => {
            const q = Number(qty);
            if (unit === 'eded') return `${q} əd`;
            if (unit === 'kg') return q < 1 ? `${Math.round(q * 1000)} q` : `${q} kq`;
            if (unit === 'litr' || unit === 'lt') return q < 1 ? `${Math.round(q * 1000)} ml` : `${q} lt`;
            return `${q} ${unit}`;
        };

        const title = mode === 'empty' ? 'Texkarta (Boş)' : 'Texkarta (Dolu)';
        const css = `
            @page { size: A4 landscape; margin: 12mm; }
            body { font-family: 'Times New Roman', serif; font-size: 11pt; color: #000; }
            h1 { font-size: 16pt; margin: 0 0 4pt; text-align: center; }
            .meta { text-align: center; font-size: 9pt; color: #555; margin-bottom: 14pt; }
            .printer-title {
                font-size: 18pt; margin: 0 0 10pt; padding: 8pt 12pt;
                background: #000; color: #fff; text-align: center; letter-spacing: 1pt;
            }
            h2 { font-size: 12pt; margin: 10pt 0 4pt; padding: 4pt 8pt; background: #f0f0f0; border-left: 3pt solid #000; page-break-after: avoid; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 8pt; page-break-inside: avoid; }
            th, td { border: 1pt solid #000; padding: 4pt 6pt; vertical-align: top; }
            th { background: #e8e8e8; font-size: 10pt; text-align: left; }
            td.menu { width: 18%; font-weight: bold; }
            td.recipe { font-size: 10pt; }
            .empty-line { height: 14pt; border-bottom: 0.5pt dotted #999; margin: 2pt 0; }
            @media print {
                h2, .printer-title { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            }
        `;

        // Printer sırası (vacib olanlar əvvəl)
        const printerOrder = ['xp-istimetbex', 'xp-soyuq', 'xp-manqal', 'xp-bar', 'xp-cayci', 'xp-ofisiant'];
        const allPrinters = Object.keys(byPrinter);
        const orderedPrinters = [
            ...printerOrder.filter(p => allPrinters.includes(p)),
            ...allPrinters.filter(p => !printerOrder.includes(p))
        ];

        if (!orderedPrinters.length) {
            toast.error('Çap üçün məhsul yoxdur.');
            return;
        }

        // Aktiv printerləri DB-dən yüklə (xp-istimetbex və s. → real Windows printer adı)
        const printerList = await loadPrinters();
        const printerByName = {};
        printerList.forEach(p => { printerByName[p.name] = p; });

        let printedCount = 0;
        let skippedCount = 0;

        // HƏR PRINTER ÜÇÜN AYRI SİLENT ÇAP
        for (const printer of orderedPrinters) {
            const cats = byPrinter[printer];
            const printerLabel = PRINTER_NAMES[printer] || printer;
            const printerItemCount = Object.values(cats).reduce((s, arr) => s + arr.length, 0);

            // Printer sistemə qoşulmamış olsa → skip
            const printerInfo = printerByName[printer];
            if (!printerInfo && window.chrome?.webview?.postMessage) {
                skippedCount++;
                continue;
            }

            // HTML bu printer üçün
            let html = `<!doctype html><html><head><meta charset="utf-8"><title>${title} — ${printerLabel}</title><style>${css}</style></head><body>`;
            html += `<h1>${title}</h1><div class="meta">${dateStr} — ${printerLabel}</div>`;
            html += `<div class="printer-title">🖨️ ${printerLabel} — ${printerItemCount} məhsul</div>`;

            // Kateqoriyalar
            const categories = Object.keys(cats).sort();
            for (const cat of categories) {
                const items = cats[cat];
                if (!items.length) continue;
                html += `<h2>${cat}</h2><table>`;
                html += `<thead><tr><th class="menu" style="width:18%">Məhsul</th><th>İnqredientlər</th></tr></thead><tbody>`;

                for (const item of items) {
                    let recipeStr = '';
                    if (mode === 'full') {
                        const recipe = recipeMap[item.id] || [];
                        if (recipe.length) {
                            recipeStr = recipe.map(r => `${r.name} ${fmtQty(r.qty, r.unit)}`).join(' ; ');
                        } else {
                            recipeStr = '<em style="color:#888">(texkarta yoxdur)</em>';
                        }
                    } else {
                        recipeStr = '<div class="empty-line"></div><div class="empty-line"></div>';
                    }
                    html += `<tr><td class="menu">${item.name}</td><td class="recipe">${recipeStr}</td></tr>`;
                }
                html += `</tbody></table>`;
            }
            html += `</body></html>`;

            // C# silent print bridge (or browser fallback)
            doPrintHtml(html, printer);
            printedCount++;

            // Hər çap arası 500ms (printer queue rahat olsun)
            await new Promise(r => setTimeout(r, 500));
        }

        if (window.chrome?.webview?.postMessage) {
            toast.success(`${printedCount} printer-ə göndərildi${skippedCount ? ` (${skippedCount} qoşulmayıb)` : ''}.`);
        }
    };

    // İşçilər üçün depo-depo boş sayım vərəqi çap edir.
    // Hər depo öz səhifəsindən başlayır, sağ üstdə səhifə sayğacı.
    const printBlankCountSheet = async () => {
        if (!sayimData.length) {
            toast.error('İnqredient yoxdur.');
            return;
        }

        const unitLabel = (u) => ({ gram: 'qram', ml: 'ml', eded: 'ədəd', kg: 'kq', litr: 'litr' }[u] || u || '');

        // Group by depot
        const groups = {};
        for (const row of sayimData) {
            const key = row.depot_name || 'Digər';
            if (!groups[key]) groups[key] = [];
            groups[key].push(row);
        }

        const ROWS_PER_PAGE = 28; // A4-də sığacaq say
        let totalPages = 0;
        Object.values(groups).forEach(rows => {
            totalPages += Math.max(1, Math.ceil(rows.length / ROWS_PER_PAGE));
        });

        const todayStr = new Date().toLocaleDateString('az-AZ', {
            day: '2-digit', month: 'long', year: 'numeric'
        });

        let pageNum = 0;
        let html = `
            <!DOCTYPE html>
            <html lang="az"><head><meta charset="utf-8"><title>Sayım vərəqi</title>
            <style>
                @page { size: A4; margin: 12mm 10mm 10mm 10mm; }
                * { box-sizing: border-box; }
                body { font-family: 'Arial', sans-serif; margin: 0; color: #000; }
                .page { page-break-after: always; }
                .page:last-child { page-break-after: auto; }
                .header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; padding-bottom: 8px; border-bottom: 2px solid #000; }
                .depot-name { font-size: 18px; font-weight: bold; }
                .page-info { font-size: 11px; font-style: italic; }
                .meta { font-size: 11px; color: #555; margin-bottom: 8px; }
                table { width: 100%; border-collapse: collapse; }
                th { background: #f0f0f0; text-align: left; padding: 6px 8px; font-size: 11px; border: 1px solid #999; }
                td { padding: 7px 8px; font-size: 12px; border: 1px solid #999; }
                td.name { font-weight: bold; }
                td.type { color: #555; font-size: 11px; }
                td.x { text-align: center; width: 40px; font-weight: bold; color: #c00; }
                td.count { width: 110px; }
            </style></head><body>
        `;

        for (const [depotName, rows] of Object.entries(groups)) {
            for (let i = 0; i < rows.length; i += ROWS_PER_PAGE) {
                pageNum++;
                const slice = rows.slice(i, i + ROWS_PER_PAGE);
                html += `
                    <div class="page">
                        <div class="header">
                            <div class="depot-name">🏪 ${depotName}</div>
                            <div class="page-info"><strong>${pageNum}.</strong> səhifə / ${totalPages}</div>
                        </div>
                        <div class="meta">Tarix: ${todayStr} &nbsp;&nbsp; Sayım edən: ___________________________</div>
                        <table>
                            <thead>
                                <tr>
                                    <th style="width: 40%">İnqredient</th>
                                    <th style="width: 15%">Vahid</th>
                                    <th style="width: 25%">Sayılan miqdar</th>
                                    <th style="width: 20%">Qeyd</th>
                                </tr>
                            </thead>
                            <tbody>
                `;
                for (const r of slice) {
                    html += `
                        <tr>
                            <td class="name">${r.name}</td>
                            <td class="type">${unitLabel(r.unit)}</td>
                            <td class="count">&nbsp;</td>
                            <td>&nbsp;</td>
                        </tr>
                    `;
                }
                html += `</tbody></table></div>`;
            }
        }

        html += `</body></html>`;

        // A4 printerini tap
        const list = await loadPrinters();
        const a4List = list.filter(p => p.printer_type === 'a4' && p.active);
        if (a4List.length === 0) {
            toast.error('A4 printer təyin olunmayıb. Admin → Printerlər səhifəsində A4 printer əlavə edin.');
            return;
        }

        const sendToPrinter = (printerName) => {
            doPrintHtml(html, printerName);
            toast.success(`Sayım vərəqi ${printerName}-ə göndərildi.`);
        };

        if (a4List.length === 1) {
            // Tək A4 → avtomatik
            sendToPrinter(a4List[0].name);
            return;
        }

        // Birdən çox A4 → istifadəçi seçim modalı
        setPrinters(a4List);
        setPendingPrintData({ __blankSheet: true, html });
        setSelectedPrinter('');
        setPrintSelectModal(true);
    };

    const handleSayimPrint = (resultData, printerName) => {
        const now     = new Date();
        const dateStr = now.toLocaleDateString('az-AZ', { day: '2-digit', month: 'long', year: 'numeric' })
            + ' ' + now.toLocaleTimeString('az-AZ', { hour: '2-digit', minute: '2-digit' });

        const fmtNum = (v, unit) => {
            const num = Number(v || 0);
            if (unit === 'eded') return `${Math.round(num)} əd`;
            const fix = n => n.toFixed(3).replace(/\.?0+$/, '');
            if (unit === 'gram') {
                if (num >= 1000) { const kg = Math.floor(num / 1000); const g = Math.round(num % 1000); return g > 0 ? `${kg}kq ${g}q` : `${kg}kq`; }
                return `${fix(num)}q`;
            }
            if (unit === 'ml') {
                if (num >= 1000) { const lt = Math.floor(num / 1000); const ml = Math.round(num % 1000); return ml > 0 ? `${lt}lt ${ml}ml` : `${lt}lt`; }
                return `${fix(num)}ml`;
            }
            if (unit === 'kg')   return `${fix(num)}kq`;
            if (unit === 'litr') return `${fix(num)}lt`;
            return fix(num);
        };

        const ulMap = { gram: 'q', ml: 'ml', eded: 'əd', kg: 'kq', litr: 'lt' };

        const isResult = Array.isArray(resultData);
        const rows     = isResult
            ? resultData
            : sayimData.map(r => ({ ...r, sayilan: null, ferq: null, hasSayilan: false, gozlenilen: Number(r.qaliq || 0) }));

        // Group by depot
        const grouped = {};
        rows.forEach(row => {
            const key = row.depot_name || '__none__';
            if (!grouped[key]) grouped[key] = { label: row.depot_name || null, rows: [] };
            grouped[key].rows.push(row);
        });
        const groups = Object.values(grouped);

        let rowIdx = 0;
        const tableRows = groups.map(g => {
            const header = g.label
                ? `<tr><td colspan="12" style="background:#1e293b;color:#fff;font-weight:700;font-size:11px;padding:5px 8px">🏪 ${g.label}</td></tr>`
                : '';
            const dataRows = g.rows.map(row => {
                const fmt = v => fmtNum(v, row.unit);
                const ul  = ulMap[row.unit] || row.unit;
                const bg  = rowIdx++ % 2 === 0 ? '#fff' : '#f8fafc';
                if (isResult) {
                    const ferqColor = row.ferq === null ? '' : row.ferq > 0 ? '#16a34a' : row.ferq < 0 ? '#dc2626' : '#374151';
                    return `<tr style="background:${bg}">
                        <td style="font-weight:600">${row.name}</td>
                        <td style="text-align:center;color:#888">${ul}</td>
                        <td style="text-align:right">${fmt(row.kecan_sayim)}</td>
                        <td style="text-align:right;color:#16a34a">${row.medaxil > 0 ? fmt(row.medaxil) : '—'}</td>
                        <td style="text-align:right;color:#f97316">${row.medaxil_itki > 0 ? fmt(row.medaxil_itki) : '—'}</td>
                        <td style="text-align:right">${row.istifade > 0 ? fmt(row.istifade) : '—'}</td>
                        <td style="text-align:right;color:#ef4444">${row.itki > 0 ? fmt(row.itki) : '—'}</td>
                        <td style="text-align:right;color:#d97706">${row.terg > 0 ? fmt(row.terg) : '—'}</td>
                        <td style="text-align:right;color:#9333ea">${row.prefaktoriya > 0 ? fmt(row.prefaktoriya) : '—'}</td>
                        <td style="text-align:right;font-weight:700;color:${row.gozlenilen < 0 ? '#dc2626' : '#0284c7'}">${fmt(row.gozlenilen)}</td>
                        <td style="text-align:right;font-weight:700;color:#4f46e5">${row.hasSayilan ? fmt(row.sayilan) : ''}</td>
                        <td style="text-align:right;color:${ferqColor};font-weight:700">${row.ferq !== null ? (row.ferq > 0 ? '+' : '') + fmt(row.ferq) : ''}</td>
                    </tr>`;
                } else {
                    return `<tr style="background:${bg}">
                        <td style="font-weight:600">${row.name}</td>
                        <td style="text-align:center;color:#888">${ul}</td>
                        <td style="text-align:right">${fmt(row.kecan_sayim)}</td>
                        <td style="text-align:right;color:#16a34a">${row.medaxil > 0 ? fmt(row.medaxil) : '—'}</td>
                        <td style="text-align:right;color:#f97316">${row.medaxil_itki > 0 ? fmt(row.medaxil_itki) : '—'}</td>
                        <td style="text-align:right">${row.istifade > 0 ? fmt(row.istifade) : '—'}</td>
                        <td style="text-align:right;color:#ef4444">${row.itki > 0 ? fmt(row.itki) : '—'}</td>
                        <td style="text-align:right;color:#d97706">${row.terg > 0 ? fmt(row.terg) : '—'}</td>
                        <td style="text-align:right;color:#9333ea">${row.prefaktoriya > 0 ? fmt(row.prefaktoriya) : '—'}</td>
                        <td style="text-align:right;font-weight:700;color:${row.qaliq < 0 ? '#dc2626' : '#0284c7'}">${fmt(row.qaliq)}</td>
                        <td style="min-width:80px"></td>
                        <td style="min-width:60px"></td>
                    </tr>`;
                }
            }).join('');
            return header + dataRows;
        }).join('');

        const isResultCols = isResult
            ? `<th>Məhsul</th><th style="width:45px;text-align:center">Vahid</th><th style="width:80px;text-align:right">Keçən sayım</th><th style="width:70px;text-align:right">Mədaxil</th><th style="width:65px;text-align:right">Məd.itki</th><th style="width:70px;text-align:right">İstifadə</th><th style="width:55px;text-align:right">İtki</th><th style="width:55px;text-align:right">Tərg</th><th style="width:65px;text-align:right">Prefakt.</th><th style="width:80px;text-align:right">Qalıq</th><th style="width:80px;text-align:right">Sayılan</th><th style="width:65px;text-align:right">Fərq</th>`
            : `<th>Məhsul</th><th style="width:45px;text-align:center">Vahid</th><th style="width:85px;text-align:right">Keçən sayım</th><th style="width:70px;text-align:right">Mədaxil</th><th style="width:65px;text-align:right">Məd.itki</th><th style="width:70px;text-align:right">İstifadə</th><th style="width:60px;text-align:right">İtki</th><th style="width:60px;text-align:right">Tərg</th><th style="width:65px;text-align:right">Prefakt.</th><th style="width:80px;text-align:right">Qalıq</th><th style="width:85px;text-align:right">Sayılan</th><th style="width:65px;text-align:right">Fərq</th>`;

        const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Sayım Vərəqəsi</title>
<style>
@page{size:A4 landscape;margin:10mm}
*{box-sizing:border-box}
body{font-family:Arial,sans-serif;font-size:11px;color:#111;margin:0}
h2{text-align:center;margin:0 0 3px;font-size:15px;font-weight:700}
.sub{text-align:center;color:#888;font-size:10px;margin-bottom:10px}
table{width:100%;border-collapse:collapse}
th{background:#1e293b;color:#fff;padding:5px 8px;text-align:left;font-size:10px;text-transform:uppercase;white-space:nowrap}
td{padding:4px 8px;border-bottom:1px solid #e5e7eb;font-size:11px}
</style></head><body>
<h2>Sayım Vərəqəsi</h2>
<div class="sub">${dateStr}</div>
<table><thead><tr>${isResultCols}</tr></thead>
<tbody>${tableRows}</tbody></table>
</body></html>`;

        doPrintHtml(html, printerName);
    };

    // ── İNQREDİENT ────────────────────────────────────────────
    const openIngCreate = () => {
        setIngForm({ name: '', unit: 'eded', stock: 0, min_stock: 0, cost_per_unit: 0, mexaric_itki_percent: 0, medaxil_itki_percent: 0, depot_id: '' });
        setEditIng(null);
        recipeAutoSelectRef.current = false;
        setIngModal(true);
    };

    const openIngEdit = (ing) => {
        setIngForm({
            name:                 ing.name,
            unit:                 ing.unit,
            stock:                Number(ing.stock),
            min_stock:            Number(ing.min_stock),
            cost_per_unit:        Number(ing.cost_per_unit),
            mexaric_itki_percent: Number(ing.mexaric_itki_percent || 0),
            medaxil_itki_percent: Number(ing.medaxil_itki_percent || 0),
            depot_id:             ing.depot_id ? String(ing.depot_id) : '',
        });
        setEditIng(ing);
        recipeAutoSelectRef.current = false;
        setIngModal(true);
    };

    const handleIngSubmit = async (e) => {
        e.preventDefault();
        if (!ingForm.name.trim()) {
            toast.error('İnqredient adı yazın.');
            return;
        }
        try {
            if (editIng) {
                await api.put(`/ingredients/${editIng.id}`, ingForm);
                toast.success('Yeniləndi.');
                setIngModal(false);
                loadIngredients();
            } else {
                const res = await api.post('/ingredients', ingForm);
                toast.success('Əlavə edildi.');
                setIngModal(false);
                // Texkarta axını: yeni inqredienti reseptə avtomatik seç, miqdar yerinə fokus
                if (recipeAutoSelectRef.current) {
                    recipeAutoSelectRef.current = false;
                    const newId = res.data?.id;
                    try {
                        const listRes = await api.get('/ingredients/all');
                        const list = listRes.data.data || [];
                        setIngredients(list);
                        const created = list.find(x => String(x.id) === String(newId));
                        if (created) selectRecipeIng(created);
                    } catch { loadIngredients(); }
                } else {
                    loadIngredients();
                }
            }
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Xəta baş verdi.');
        }
    };

    const handleIngDelete = async () => {
        if (!deleteIngredient) return;
        try {
            await api.delete(`/ingredients/${deleteIngredient.id}`);
            toast.success('Silindi.');
            setDeleteIngredient(null);
            loadIngredients();
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Silinə bilmədi.');
        }
    };

    // ── STOK ──────────────────────────────────────────────────
    const openStockModal = (ing) => {
        setSelectedIng(ing);
        setStockForm({ type: 'giris', quantity: '', note: '' });
        setStockModal(true);
    };

    const handleStockSubmit = async (e) => {
        e.preventDefault();
        if (!stockForm.quantity && stockForm.quantity !== 0) {
            toast.error('Miqdar daxil edin.');
            return;
        }
        try {
            await api.post(`/ingredients/${selectedIng.id}/stock`, stockForm);
            toast.success('Stok yeniləndi.');
            setStockModal(false);
            loadIngredients();
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Xəta baş verdi.');
        }
    };

    // ── TEXKARTA ──────────────────────────────────────────────
    const handleSelectMenuItem = async (item) => {
        // Freeze sort order at the moment user clicks a new item
        setRecipeSortSnapshot(new Set(allRecipes.map(r => r.menu_item_id)));
        // Load variants if not cached
        if (!itemVariants[item.id]) {
            try {
                const res = await api.get(`/menu/items/${item.id}/variants`);
                const vars = res.data.data || [];
                setItemVariants(prev => ({ ...prev, [item.id]: vars }));
                if (vars.length > 0) {
                    // Has variants → show picker
                    setVariantPickModal({ item, variants: vars });
                    return;
                }
            } catch { /* ignore */ }
        } else if ((itemVariants[item.id] || []).length > 0) {
            setVariantPickModal({ item, variants: itemVariants[item.id] });
            return;
        }
        // No variants — load base recipe
        setSelectedItem(item);
        setSelectedVariant(null);
        await loadRecipes(item.id, null);
    };

    const handleSelectVariant = async (item, variant) => {
        setVariantPickModal(null);
        setRecipeSortSnapshot(new Set(allRecipes.map(r => r.menu_item_id)));
        setSelectedItem(item);
        setSelectedVariant(variant); // null = base item
        setRecipeForm({ ingredient_id: '', quantity: '' });
        setRecipeSearch('');
        setRecipeMidSearch('');
        if (variant) {
            await loadRecipes(item.id, variant.id);
        } else {
            await loadRecipes(item.id, null);
        }
    };

    const handleRecipeAdd = async (e) => {
        e.preventDefault();
        if (!selectedItem) return;
        if (!recipeForm.ingredient_id) { toast.error('İnqredient seçin.'); return; }
        if (!recipeForm.quantity && recipeForm.quantity !== 0) { toast.error('Miqdar daxil edin.'); return; }
        // Yazılan vahiddən (qram/ml ya kq/lt) baza vahidinə çevir
        const addIng = ingredients.find(i => i.id === Number(recipeForm.ingredient_id));
        const baseQty = recipeToBase(recipeForm.quantity, addIng?.unit, recipeUnitMode);
        if (!(baseQty > 0)) { toast.error('Miqdar 0-dan böyük olmalıdır.'); return; }
        try {
            await api.post('/recipes', {
                menu_item_id:  selectedItem.id,
                ingredient_id: recipeForm.ingredient_id,
                quantity:      baseQty,
                variant_id:    selectedVariant?.id || null
            });
            toast.success('Texkarta yeniləndi.');
            setRecipeForm({ ingredient_id: '', quantity: '' });
            setRecipeSearch('');
            setRecipeHighlight(-1);
            setTimeout(() => recipeSearchRef.current?.focus(), 60); // növbəti inqredient üçün axtarışa qayıt
            await loadRecipes(selectedItem.id, selectedVariant?.id || null);
            // Refresh allRecipes
            api.get('/recipes/all').then(r => setAllRecipes(r.data.data || [])).catch(() => {});
        } catch (err) {
            toast.error(err.response?.data?.msg || 'Xəta baş verdi.');
        }
    };

    const handleRecipeDelete = async (recipeId) => {
        try {
            await api.delete(`/recipes/${recipeId}`);
            toast.success('Silindi.');
            await loadRecipes(selectedItem.id, selectedVariant?.id || null);
            api.get('/recipes/all').then(r => setAllRecipes(r.data.data || [])).catch(() => {});
        } catch {
            toast.error('Silinmədi.');
        }
    };

    const unitLabel = (u) => {
        const map = { gram: 'q', ml: 'ml', eded: 'əd', kg: 'kq', litr: 'lt' };
        return map[u] || u;
    };

    // Mədaxil itkisi tənzimlənən faizi qram/ml qarşılığı ilə (0.25 kq → "250q/kq") — məlumat üçün
    const medItkiRate = (unit, pct) => {
        const p = Number(pct) || 0;
        if (p <= 0) return '';
        if (unit === 'kg')                    return `${+(p * 1000).toFixed(1)}q/kq`;
        if (unit === 'litr' || unit === 'lt') return `${+(p * 1000).toFixed(1)}ml/lt`;
        if (unit === 'gram')                  return `${+p.toFixed(2)}q/kq`;
        if (unit === 'ml')                    return `${+p.toFixed(2)}ml/lt`;
        return `×${+p.toFixed(2)}`; // eded
    };

    // Format a number with max `digits` decimal places, stripping trailing zeros
    const trimNumber = (value, digits = 4) => {
        const num = Number(value || 0);
        return num.toFixed(digits).replace(/\.?0+$/, '');
    };

    const formatByUnit = (value, unit) => {
        const num = Number(value || 0);

        if (unit === 'eded') return `${trimNumber(num)}əd`;

        if (unit === 'gram') {
            if (num >= 1000) {
                const kg = Math.floor(num / 1000);
                const gr = Math.round(num % 1000);
                return gr > 0 ? `${kg}kq ${gr}q` : `${kg}kq`;
            }
            return `${trimNumber(num)}q`;
        }

        if (unit === 'ml') {
            if (num >= 1000) {
                const lt = Math.floor(num / 1000);
                const ml = Math.round(num % 1000);
                return ml > 0 ? `${lt}lt ${ml}ml` : `${lt}lt`;
            }
            return `${trimNumber(num)}ml`;
        }

        if (unit === 'kg')   return `${trimNumber(num)}kq`;
        if (unit === 'litr') return `${trimNumber(num)}lt`;

        return `${trimNumber(num)} ${unitLabel(unit)}`;
    };

    // Texkarta miqdar formatı: kg<1 → qram, litr<1 → ml; ≥1 → kq/lt (eded/gram/ml dəyişməz)
    const fmtRecipeQty = (qty, unit) => {
        const q = Number(qty) || 0;
        if (unit === 'kg')   return q < 1 ? `${trimNumber(q * 1000)}q`  : `${trimNumber(q)}kq`;
        if (unit === 'litr') return q < 1 ? `${trimNumber(q * 1000)}ml` : `${trimNumber(q)}lt`;
        return formatByUnit(q, unit);
    };
    // kg/litr → kiçik vahidlə (qram/ml) yazıla bilər
    const isSmallableUnit = (u) => u === 'kg' || u === 'litr';
    // display (yazılan) → base (DB): small modda kg/litr ÷1000
    const recipeToBase = (disp, unit, mode) => {
        const d = Number(disp) || 0;
        return (isSmallableUnit(unit) && mode === 'small') ? d / 1000 : d;
    };
    // base (DB) → display (yazılan)
    const recipeToDisplay = (base, unit, mode) => {
        const b = Number(base) || 0;
        return (isSmallableUnit(unit) && mode === 'small') ? b * 1000 : b;
    };

    // İnqredient seç (klik/Enter) → forma açılır, default qram/ml, miqdar input-una fokus
    // Texkarta axını: "Yeni inqredient" pəncərəsini axtarışdakı adla aç,
    // yaradıldıqdan sonra həmin inqredienti reseptə avtomatik seçmək üçün flag
    const recipeAutoSelectRef = useRef(false);
    const openIngCreateForRecipe = (prefillName) => {
        if (!selectedItem) { toast.error('Əvvəlcə məhsul seçin.'); return; }
        setIngForm({ name: (prefillName || '').trim(), unit: 'eded', stock: 0, min_stock: 0, cost_per_unit: 0, mexaric_itki_percent: 0, medaxil_itki_percent: 0, depot_id: '' });
        setEditIng(null);
        recipeAutoSelectRef.current = true;
        setIngModal(true);
    };

    const selectRecipeIng = (ing) => {
        if (!ing) return;
        if (!selectedItem) { toast.error('Əvvəlcə məhsul seçin.'); return; }
        const existingRow = recipes.find(r => r.ingredient_id === ing.id);
        setRecipeUnitMode('small');
        const disp = existingRow ? recipeToDisplay(existingRow.quantity, ing.unit, 'small') : '';
        setRecipeForm({ ingredient_id: String(ing.id), quantity: disp === '' ? '' : String(trimNumber(disp)) });
        setRecipeSearch(ing.name);
        setRecipeHighlight(-1);
        setTimeout(() => recipeQtyRef.current?.focus(), 60); // miqdar yerinə fokus
    };

    const formatMoney = (value) => trimNumber(value, 2);

    // Show cost per natural unit: gram→per kg, ml→per lt, others as-is
    const formatCostPerUnit = (costPerUnit, unit) => {
        const c = Number(costPerUnit || 0);
        if (!c) return '—';
        // Vahid qiymət maks 2 onluq (##.##), sondakı sıfırlar atılır
        if (unit === 'gram') return `${trimNumber(c * 1000, 2)} ₼/kq`;
        if (unit === 'ml')   return `${trimNumber(c * 1000, 2)} ₼/lt`;
        if (unit === 'kg')   return `${trimNumber(c, 2)} ₼/kq`;
        if (unit === 'litr') return `${trimNumber(c, 2)} ₼/lt`;
        return `${trimNumber(c, 2)} ₼/əd`;
    };

    const inputClass = "w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary-500 transition-all";
    const ingredientStep = ingForm.unit === 'eded' ? '1' : '0.001';
    const filteredIngredients = ingredients.filter((ing) => {
        const matchSearch = ing.name.toLowerCase().includes(stockSearch.toLowerCase());
        const matchDepot = !stockDepotFilter
            ? true
            : stockDepotFilter === 'null_depot'
                ? !ing.depot_id
                : String(ing.depot_id || '') === stockDepotFilter;
        return matchSearch && matchDepot;
    });
    const filteredHistoryIngredients = ingredients.filter((ing) =>
        ing.name.toLowerCase().includes(historySearch.toLowerCase())
    );
    const movementUnit = ingredients.find((ing) => ing.id === selectedIngId)?.unit;
    const movementRows = (() => {
        if (!selectedIngId || movements.length === 0) return [];

        let balance = 0;
        const ascending = [...movements].reverse().map((movement) => {
            const quantity = Number(movement.quantity || 0);
            const balanceBefore = balance;
            if (movement.type === 'duzeltme') {
                balance = quantity;
            } else if (movement.type === 'giris') {
                balance += quantity;
            } else {
                balance -= quantity;
            }

            return {
                ...movement,
                balance_before: balanceBefore,
                change_label:
                    movement.type === 'duzeltme'
                        ? `${formatByUnit(balanceBefore, movementUnit)} → ${formatByUnit(balance, movementUnit)}`
                        : movement.type === 'giris'
                            ? `${formatByUnit(balanceBefore, movementUnit)} + ${formatByUnit(quantity, movementUnit)}`
                            : `${formatByUnit(balanceBefore, movementUnit)} - ${formatByUnit(quantity, movementUnit)}`,
                balance_after: balance
            };
        });

        return ascending.reverse();
    })();

    return (
        <Layout>
            <UpgradeModal
                open={showUpgradeModal}
                onClose={() => setShowUpgradeModal(false)}
                title="Anbar və texkarta yalnız Pro paketindədir"
                message="İnqredient, stok və texkarta modulları üçün Pro plana keçin."
                plans={['Pro']}
            />

            <div>

                {/* TABS — mobildə yan-sürüşən (bütün tab-lara çatmaq üçün) */}
                <div className="flex gap-2 mb-6 overflow-x-auto pb-2 [&>button]:shrink-0 [&>button]:whitespace-nowrap">
                    {(isKassirView
                        ? [
                            { key: 'stock',   label: '📦 Stok idarəsi' },
                            { key: 'history', label: '📊 Hərəkət tarixi' },
                          ]
                        : [
                            { key: 'menu',    label: '🍽️ Menyu idarəsi' },
                            { key: 'recipe',  label: '📋 Texkarta' },
                            { key: 'stock',   label: '📦 Stok idarəsi' },
                            { key: 'history', label: '📊 Hərəkət tarixi' },
                            { key: 'sayim',   label: '🧮 Sayım' },
                          ]
                    ).map(t => (
                        <button
                            key={t.key}
                            onClick={() => {
                                if (!canUseWarehouse && t.key !== 'menu') {
                                    setShowUpgradeModal(true);
                                    return;
                                }
                                setTab(t.key);
                            }}
                            className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all ${
                                tab === t.key
                                    ? 'bg-primary-500 text-white'
                                    : 'bg-white border border-gray-200 text-gray-500 hover:border-primary-300'
                            }`}
                        >
                            {!canUseWarehouse && t.key !== 'menu' ? `${t.label} 🔒` : t.label}
                        </button>
                    ))}
                </div>

                {/* ── STOK İDARƏSİ ── */}
                {tab === 'stock' && (
                    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                            <h3 className="font-bold text-gray-800">📦 Anbar stoku</h3>
                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    onClick={() => setLossCalcOpen(true)}
                                    className="px-4 py-2 rounded-xl border border-gray-200 bg-white text-sm font-bold text-gray-700 hover:bg-gray-50 transition-all"
                                >
                                    🧮 İtki hesabla
                                </button>
                                {isAdmin && (
                                    <button
                                        onClick={openIngCreate}
                                        className="px-4 py-2 rounded-xl text-sm font-bold text-white transition-all"
                                        style={{ background: 'linear-gradient(135deg, #006f91, #0090bb)' }}
                                    >
                                        + Yeni inqredient
                                    </button>
                                )}
                            </div>
                        </div>
                        <div className="px-5 pb-4 flex gap-3">
                            <input
                                type="text"
                                value={stockSearch}
                                onChange={e => setStockSearch(e.target.value)}
                                placeholder="🔍 Anbarda axtar..."
                                className={`${inputClass} flex-1`}
                            />
                            {depots.length > 0 && (
                                <select
                                    value={stockDepotFilter}
                                    onChange={e => setStockDepotFilter(e.target.value)}
                                    className="px-3 py-2 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary-500 transition-all bg-white"
                                >
                                    <option value="">🏪 Bütün depolar</option>
                                    {depots.map(d => (
                                        <option key={d.id} value={String(d.id)}>{d.name}</option>
                                    ))}
                                    <option value="null_depot">— Depossuz</option>
                                </select>
                            )}
                        </div>

                        {loading ? (
                            <div className="p-12 text-center text-gray-400">Yüklənir...</div>
                        ) : filteredIngredients.length === 0 ? (
                            <div className="p-12 text-center text-gray-400">
                                Hələ inqredient əlavə edilməyib.
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                            <table className="w-full min-w-[760px]">
                                <thead>
                                    <tr className="bg-gray-50">
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Ad</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Depo</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Vahid</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Stok</th>
                                        {!isKassirView && <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Min stok</th>}
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Alış qiyməti</th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Status</th>
                                        {!isKassirView && <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Əməliyyat</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredIngredients.map(ing => (
                                        <tr key={ing.id} className="border-t border-gray-50 hover:bg-gray-50">
                                            <td className="px-4 py-3 font-semibold text-sm text-gray-800">
                                                {ing.name}
                                            </td>
                                            <td className="px-4 py-3 text-xs text-gray-400">
                                                {ing.depot_name
                                                    ? <span className="px-2 py-0.5 rounded-full bg-orange-50 text-orange-600 font-semibold border border-orange-100">{ing.depot_name}</span>
                                                    : <span className="text-gray-300">—</span>}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-500">
                                                {unitLabel(ing.unit)}
                                            </td>
                                            <td className="px-4 py-3">
                                                {(() => {
                                                    const sayimRow = sayimData.find(s => s.id === ing.id);
                                                    const displayQty = sayimRow != null ? sayimRow.qaliq : ing.stock;
                                                    const isLow = displayQty <= ing.min_stock;
                                                    return (
                                                        <span className={`font-bold font-mono text-sm ${isLow ? 'text-red-500' : 'text-gray-800'}`}>
                                                            {formatByUnit(displayQty, ing.unit)}
                                                        </span>
                                                    );
                                                })()}
                                            </td>
                                            {!isKassirView && (
                                            <td className="px-4 py-3 text-sm text-gray-400 font-mono">
                                                {formatByUnit(ing.min_stock, ing.unit)}
                                            </td>
                                            )}
                                            <td className="px-4 py-3 text-sm text-gray-700 font-mono font-semibold">
                                                {formatCostPerUnit(ing.last_purchase_price ?? ing.cost_per_unit, ing.unit)}
                                            </td>
                                            <td className="px-4 py-3">
                                                {(() => {
                                                    const sayimRow = sayimData.find(s => s.id === ing.id);
                                                    const isLow = sayimRow != null
                                                        ? sayimRow.qaliq <= ing.min_stock
                                                        : !!ing.low_stock;
                                                    return isLow ? (
                                                        <span className="text-xs font-bold px-2 py-1 rounded-full bg-red-100 text-red-600">
                                                            ⚠️ Az qalıb
                                                        </span>
                                                    ) : (
                                                        <span className="text-xs font-bold px-2 py-1 rounded-full bg-green-100 text-green-600">
                                                            ✅ Normal
                                                        </span>
                                                    );
                                                })()}
                                            </td>
                                            {!isKassirView && (
                                            <td className="px-4 py-3">
                                                <div className="flex gap-1.5">
                                                    <button
                                                        onClick={() => openStockModal(ing)}
                                                        className="px-2.5 py-1 rounded-lg text-xs font-semibold bg-primary-50 text-primary-600 hover:bg-primary-100 transition-all"
                                                    >
                                                        📦 Stok
                                                    </button>
                                                    <button
                                                        onClick={() => { setTab('history'); loadMovements(ing.id); }}
                                                        className="px-2.5 py-1 rounded-lg text-xs font-semibold bg-gray-100 text-gray-600 hover:bg-gray-200 transition-all"
                                                    >
                                                        📊
                                                    </button>
                                                    {isAdmin && (
                                                        <button
                                                            onClick={() => openIngEdit(ing)}
                                                            className="px-2.5 py-1 rounded-lg text-xs font-semibold bg-gray-100 text-gray-600 hover:bg-gray-200 transition-all"
                                                        >
                                                            ✏️
                                                        </button>
                                                    )}
                                                    {isAdmin && (
                                                        <button
                                                            onClick={() => setDeleteIngredient(ing)}
                                                            className="px-2.5 py-1 rounded-lg text-xs font-semibold bg-red-50 text-red-500 hover:bg-red-100 transition-all"
                                                        >
                                                            🗑️
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            </div>
                        )}
                    </div>
                )}

                {/* ── TEXKARTA ── */}
                {tab === 'recipe' && (() => {
                    const itemsWithRecipe = new Set(allRecipes.map(r => r.menu_item_id));
                    // Use snapshot for sort order (frozen while editing); fall back to live set initially
                    const sortSet = recipeSortSnapshot ?? itemsWithRecipe;

                    // Left: menu items — no-recipe first (by sortSet), has-recipe below; stable while editing
                    const leftQ = recipeLeftSearch.trim().toLowerCase();
                    const isIngMode = recipeLeftSearchMode === 'ingredient';
                    const leftIngId = Number(recipeLeftIngId) || null;
                    const sortedMenuItems = (() => {
                        // İnqredient rejimi: comboda seçilmiş DƏQİQ inqrediyenti işlədən məhsullar (substring yox)
                        const ingItemIds = (isIngMode && leftIngId)
                            ? new Set(allRecipes.filter(r => r.ingredient_id === leftIngId).map(r => r.menu_item_id))
                            : null;
                        const filtered = menuItems.filter(i => {
                            // Kateqoriya çapında seçilmiş kateqoriya (+ printer) siyahını da süzür
                            if (printCatName && i.cat_name !== printCatName) return false;
                            if (printCatName && printPrinterKey && i.printer !== printPrinterKey) return false;
                            if (isIngMode) return leftIngId ? ingItemIds.has(i.id) : true;
                            if (!leftQ) return true;
                            return i.name.toLowerCase().includes(leftQ);
                        });
                        return [
                            ...filtered.filter(i => !sortSet.has(i.id)),
                            ...filtered.filter(i =>  sortSet.has(i.id)),
                        ];
                    })();
                    // İnqredient combosu üçün seçimlər — yalnız reseptlərdə işlədilən aktiv inqredientlər
                    const recipeIngIdSet = new Set(allRecipes.map(r => r.ingredient_id));
                    const leftIngOptions = ingredients
                        .filter(i => i.active && recipeIngIdSet.has(i.id))
                        .sort((a, b) => a.name.localeCompare(b.name, 'az'))
                        .map(i => ({ value: String(i.id), label: i.name }));
                    const leftSelectedIng = ingredients.find(i => i.id === leftIngId);

                    // Middle: current recipe rows filtered by ingredient name
                    const midQ = recipeMidSearch.trim().toLowerCase();
                    // Sıra: yeməkdə TUTDUĞU pula görə bahadan ucuza (miqdar × vahid qiymət, itki daxil).
                    // Məs. quzu əti 120 q × 22.08 = 2.65 ₼ ən üstdə; sumaq 2 q × 25 = 0.05 ₼ aşağıda.
                    const lineCostOf = (r) => {
                        const ing = ingredients.find(i => i.id === r.ingredient_id);
                        const unit = ing ? Number(ing.last_purchase_price ?? ing.cost_per_unit) || 0 : 0;
                        const loss = Number(r.medaxil_itki_percent || ing?.medaxil_itki_percent || 0);
                        return unit * itkiFactor(loss, ing?.unit) * (Number(r.quantity) || 0);
                    };
                    const filteredRecipes = recipes
                        .filter(r => !midQ || r.ingredient_name?.toLowerCase().includes(midQ))
                        .slice()
                        .sort((a, b) => (lineCostOf(b) - lineCostOf(a))
                            || String(a.ingredient_name || '').localeCompare(String(b.ingredient_name || ''), 'az'));

                    // Right: all active ingredients filtered by search
                    const rightQ = recipeSearch.trim().toLowerCase();
                    const filteredRightIngs = ingredients
                        .filter(i => i.active)
                        .filter(i => !rightQ || i.name.toLowerCase().includes(rightQ));

                    const selectedIngredient = ingredients.find(i => i.id === Number(recipeForm.ingredient_id));
                    // Yazma vahidi: kg/litr üçün qram/ml (small) ya kq/lt (base); başqa vahidlər həmişə öz vahidi
                    const recSmallable = isSmallableUnit(selectedIngredient?.unit);
                    const recMode = recSmallable ? recipeUnitMode : 'base';
                    const recDispUnit = !selectedIngredient ? ''
                        : (recSmallable
                            ? (recMode === 'small' ? (selectedIngredient.unit === 'kg' ? 'qram' : 'ml')
                                                    : (selectedIngredient.unit === 'kg' ? 'kq' : 'lt'))
                            : unitLabel(selectedIngredient.unit));
                    const recBaseQty = recipeToBase(recipeForm.quantity, selectedIngredient?.unit, recMode);
                    // step: small (qram/ml) və ədəd → 'any'; base kg/lt → '0.001'
                    const recipeStep = (selectedIngredient?.unit === 'eded' || recMode === 'small') ? 'any' : '0.001';

                    const panelH = 'h-[calc(100vh-200px)]';

                    return (
                    <>
                    {/* Menyudan gəlindisə "Geri" düyməsi (normal texkarta açılışında görünmür) */}
                    {menuReturnItemId && (
                        <button
                            onClick={() => navigate(`/admin/menu?focus=${menuReturnItemId}`)}
                            className="mb-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-gray-100 text-gray-600 hover:bg-gray-200 transition-all"
                        >
                            ← Menyuya qayıt
                        </button>
                    )}
                    <div className="grid grid-cols-1 lg:grid-cols-7 gap-3">

                        {/* ── LEFT: Menu items ── */}
                        <div className={`lg:col-span-2 bg-white rounded-xl border border-gray-200 flex flex-col overflow-hidden ${panelH}`}>
                            <div className="px-4 py-3 border-b border-gray-100 flex-shrink-0">
                                <p className="font-bold text-gray-800 text-sm mb-2">🍽️ Məhsul seç</p>
                                <div className="flex gap-1 mb-2">
                                    <button
                                        onClick={() => { setRecipeLeftSearchMode('item'); setRecipeLeftSearch(''); setRecipeLeftIngId(''); }}
                                        className={`flex-1 py-1 rounded-lg text-[10px] font-bold border transition-all ${recipeLeftSearchMode === 'item' ? 'bg-primary-500 text-white border-primary-500' : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'}`}>
                                        🍽️ Məhsul
                                    </button>
                                    <button
                                        onClick={() => { setRecipeLeftSearchMode('ingredient'); setRecipeLeftSearch(''); setRecipeLeftIngId(''); }}
                                        className={`flex-1 py-1 rounded-lg text-[10px] font-bold border transition-all ${recipeLeftSearchMode === 'ingredient' ? 'bg-primary-500 text-white border-primary-500' : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'}`}>
                                        🧂 İnqredient
                                    </button>
                                </div>
                                {isIngMode ? (
                                    /* İnqredient rejimi: dəqiq seçim üçün axtarışlı combobox */
                                    <SearchSelect
                                        value={recipeLeftIngId}
                                        onChange={(v) => setRecipeLeftIngId(v)}
                                        options={leftIngOptions}
                                        placeholder="🧂 İnqredient seç..."
                                    />
                                ) : (
                                    <input
                                        type="text"
                                        value={recipeLeftSearch}
                                        onChange={e => setRecipeLeftSearch(e.target.value)}
                                        placeholder="🔍 Məhsul axtar..."
                                        className="w-full px-3 py-2 border border-gray-200 rounded-xl text-xs focus:outline-none focus:border-primary-400 transition-all"
                                    />
                                )}
                                {/* İnqredient rejimi: seçilmiş inqrediyenti WhatsApp ilə göndər (hansı məhsullarda nə qədər var) */}
                                {isIngMode && leftSelectedIng && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setWaIng({ id: leftSelectedIng.id, name: leftSelectedIng.name });
                                            setWaMode('ingredient');
                                            setWaNum('');
                                            setWaModal(true);
                                        }}
                                        className="mt-2 w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-[11px] font-bold text-white bg-green-600 hover:bg-green-700 transition-all"
                                        title="Seçilmiş inqrediyentin hansı məhsullarda nə qədər olduğunu WhatsApp ilə göndər">
                                        📱 «{leftSelectedIng.name}» WhatsApp göndər
                                    </button>
                                )}
                                {/* Kateqoriya texkartasını A4 çap et — axtarışlı kateqoriya + printer filtri */}
                                <div className="mt-2 space-y-1.5 rounded-lg border border-gray-100 bg-gray-50 p-1.5">
                                    <SearchSelect
                                        value={printCatName}
                                        onChange={(v) => { setPrintCatName(v); setPrintPrinterKey(''); }}
                                        options={[...new Set(menuItems.map(i => i.cat_name).filter(Boolean))].sort().map(c => ({ value: c, label: c }))}
                                        placeholder="🔍 Kateqoriya çapı..."
                                    />
                                    {printCatName && (
                                        <div className="flex items-center justify-between gap-2 px-1 text-[10px] font-semibold text-primary-700">
                                            <span className="truncate">Siyahı süzülüb: {sortedMenuItems.length} məhsul</span>
                                            <button type="button" onClick={() => { setPrintCatName(''); setPrintPrinterKey(''); }}
                                                className="shrink-0 rounded-md px-1.5 py-0.5 text-gray-500 hover:bg-white hover:text-gray-700">
                                                Filtri sil
                                            </button>
                                        </div>
                                    )}
                                    <div className="flex gap-1.5">
                                        <SearchSelect
                                            value={printPrinterKey}
                                            onChange={(v) => setPrintPrinterKey(v)}
                                            className="flex-1 min-w-0"
                                            options={[
                                                { value: '', label: '🖨️ Bütün printerlər' },
                                                ...[...new Set(menuItems.filter(i => i.cat_name === printCatName && i.printer).map(i => i.printer))]
                                                    .map(p => ({ value: p, label: PRINTER_LABELS[p] || p })),
                                            ]}
                                            placeholder="Printer filtri..."
                                        />
                                        <button onClick={() => printCategoryRecipes(printCatName, printPrinterKey)}
                                            className="px-3 py-1.5 rounded-lg text-[11px] font-bold text-white bg-gray-700 hover:bg-gray-800 transition-all whitespace-nowrap shrink-0"
                                            title="Seçilmiş kateqoriyanın (və printerin) məhsullarını texkartası ilə A4 (landscape) çap et">
                                            🖨️ Çap
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div className="flex-1 overflow-y-auto p-2">
                                {sortedMenuItems.length === 0 ? (
                                    <div className="py-8 text-center text-gray-400 text-xs">Nəticə tapılmadı</div>
                                ) : sortedMenuItems.map(item => {
                                    const hasRecipe = itemsWithRecipe.has(item.id);
                                    const isSelected = selectedItem?.id === item.id;
                                    return (
                                        <div
                                            key={item.id}
                                            ref={isSelected ? selectedRowRef : null}
                                            onClick={() => handleSelectMenuItem(item)}
                                            className={`flex items-center justify-between px-3 py-2 rounded-lg cursor-pointer transition-all mb-0.5 ${
                                                isSelected
                                                    ? 'bg-primary-50 text-primary-700 border border-primary-200'
                                                    : !hasRecipe
                                                        ? 'bg-red-50 hover:bg-red-100 text-red-700 border border-red-100'
                                                        : 'hover:bg-gray-50 text-gray-700 border border-transparent'
                                            }`}
                                        >
                                            <div className="flex items-center gap-2 min-w-0">
                                                {item.image_url ? (
                                                    <img src={`${getAssetBaseUrl()}${item.image_url}`} alt=""
                                                        className="h-9 w-9 rounded-lg object-cover border border-gray-200 shrink-0" />
                                                ) : (
                                                    <div className="h-9 w-9 rounded-lg bg-gray-100 border border-gray-200 flex items-center justify-center text-gray-300 text-sm shrink-0">🍽️</div>
                                                )}
                                                <div className="min-w-0">
                                                    <p className="text-sm font-semibold truncate">{item.name}</p>
                                                    <p className="text-[11px] text-gray-400 truncate">{item.cat_name}</p>
                                                </div>
                                            </div>
                                            {!hasRecipe && (
                                                <span className="text-[10px] font-bold text-red-500 bg-red-100 rounded-full px-2 py-0.5 shrink-0 ml-1">
                                                    Yoxdur
                                                </span>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* ── MIDDLE: Current recipe ── */}
                        <div className={`lg:col-span-3 bg-white rounded-xl border border-gray-200 flex flex-col overflow-hidden ${panelH}`}>
                            <div className="px-4 py-3 border-b border-gray-100 flex-shrink-0">
                                <div className="flex items-center justify-between mb-2">
                                    <p className="font-bold text-gray-800 text-sm truncate">
                                        📋 {selectedItem
                                            ? `${selectedItem.name}${selectedVariant ? ` — ${selectedVariant.name} (override)` : ' — Ortaq texkarta'}`
                                            : 'Texkarta'}
                                    </p>
                                    {selectedItem && (itemVariants[selectedItem.id] || []).length > 0 && (
                                        <button
                                            type="button"
                                            onClick={() => setVariantPickModal({ item: selectedItem, variants: itemVariants[selectedItem.id] || [] })}
                                            className="ml-2 shrink-0 text-xs px-2 py-1 rounded-lg border border-primary-200 bg-primary-50 text-primary-600 font-semibold hover:bg-primary-100"
                                        >
                                            🔀 Variant
                                        </button>
                                    )}
                                </div>
                                {selectedItem && (
                                    <>
                                        <div className="grid grid-cols-2 gap-2 mb-2 text-[11px]">
                                            <div className="rounded-lg bg-amber-50 border border-amber-100 px-2.5 py-1.5">
                                                <div className="text-amber-600 font-semibold">💰 Maya dəyəri</div>
                                                <div className="font-mono font-bold text-amber-800">
                                                    {(() => {
                                                        const cost = recipes.reduce((s, r) =>
                                                            s + Number(r.quantity || 0) * Number(r.eff_cost_per_unit ?? r.cost_per_unit ?? 0), 0);
                                                        return cost > 0 ? `${cost.toFixed(2)} ₼` : '—';
                                                    })()}
                                                </div>
                                            </div>
                                            <div className="rounded-lg bg-emerald-50 border border-emerald-100 px-2.5 py-1.5">
                                                <div className="text-emerald-600 font-semibold">🏷️ Satış qiyməti {selectedVariant ? '(variant)' : ''}</div>
                                                {priceEditing ? (
                                                    <div className="mt-0.5 flex items-center gap-1">
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            inputMode="decimal"
                                                            autoFocus
                                                            value={priceEdit}
                                                            onChange={e => setPriceEdit(e.target.value)}
                                                            onKeyDown={e => { if (e.key === 'Enter') saveSalePrice(); if (e.key === 'Escape') setPriceEditing(false); }}
                                                            placeholder="0.00"
                                                            className="w-16 rounded border border-emerald-200 bg-white px-1.5 py-0.5 text-xs font-mono font-bold text-emerald-800 focus:outline-none focus:border-emerald-400"
                                                        />
                                                        <span className="text-[11px] font-bold text-emerald-700">₼</span>
                                                        <button type="button" onClick={saveSalePrice} disabled={priceSaving} title="Yadda saxla"
                                                            className="ml-auto rounded bg-emerald-600 px-2 py-0.5 text-[10px] font-bold text-white hover:bg-emerald-700 disabled:opacity-50">
                                                            {priceSaving ? '...' : '✓'}
                                                        </button>
                                                        <button type="button" onClick={() => setPriceEditing(false)} title="Ləğv et"
                                                            className="rounded border border-emerald-200 px-1.5 py-0.5 text-[10px] font-bold text-emerald-600 hover:bg-emerald-100">×</button>
                                                    </div>
                                                ) : (
                                                    <div className="mt-0.5 flex items-center gap-1.5">
                                                        <span className="font-mono font-bold text-emerald-800">
                                                            {Number(priceEdit) > 0 ? `${Number(priceEdit).toFixed(2)} ₼` : '—'}
                                                        </span>
                                                        <button type="button" onClick={() => setPriceEditing(true)} title="Satış qiymətini dəyiş"
                                                            className="ml-auto rounded bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-700 hover:bg-emerald-200">✏️</button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => { setWaMode('item'); setWaNum(''); setWaModal(true); }}
                                            disabled={recipes.length === 0}
                                            className="mb-2 w-full rounded-lg bg-green-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-green-700 disabled:opacity-40"
                                            title="Texkartanı WhatsApp ilə göndər"
                                        >
                                            📱 WhatsApp-a göndər
                                        </button>
                                        <input
                                            type="text"
                                            value={recipeMidSearch}
                                            onChange={e => setRecipeMidSearch(e.target.value)}
                                            placeholder="🔍 Texkartada axtar..."
                                            className="w-full px-3 py-2 border border-gray-200 rounded-xl text-xs focus:outline-none focus:border-primary-400 transition-all"
                                        />
                                    </>
                                )}
                            </div>

                            {selectedItem ? (
                                <div className="flex-1 overflow-y-auto">
                                    {filteredRecipes.length === 0 ? (
                                        <div className="py-12 text-center text-gray-400 text-sm">
                                            {recipes.length === 0
                                                ? 'Texkarta boşdur. Sağdan inqredient seçin.'
                                                : 'Axtarış nəticəsi yoxdur.'}
                                        </div>
                                    ) : (
                                        <table className="w-full">
                                            <thead className="sticky top-0 bg-gray-50 z-10">
                                                <tr>
                                                    <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-400 uppercase">İnqredient</th>
                                                    <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-400 uppercase">Miqdar</th>
                                                    <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-400 uppercase">Alış ₼</th>
                                                    <th className="w-14"></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredRecipes.map(r => {
                                                    const ing = ingredients.find(i => i.id === r.ingredient_id);
                                                    // Alış = miqdar × vahid qiymət (məs. 0.100 × 2 ₼/kq = 0.20 ₼)
                                                    const unitPrice = ing ? Number(ing.last_purchase_price ?? ing.cost_per_unit) || 0 : 0;
                                                    const lineCost  = unitPrice * (Number(r.quantity) || 0);
                                                    // Mədaxil itkisi ilə effektiv maya: alınan 1 kq-dan 0.446 itirsə, qalan 0.554 kq-ın
                                                    // qiyməti = qiymət ÷ (1 − itki).
                                                    const loss      = Number(r.medaxil_itki_percent || ing?.medaxil_itki_percent || 0);
                                                    const effUnit   = unitPrice * itkiFactor(loss, ing?.unit);
                                                    const effCost   = effUnit * (Number(r.quantity) || 0);
                                                    const isEditing = String(recipeForm.ingredient_id) === String(r.ingredient_id);
                                                    return (
                                                        <tr key={r.id} className={`border-t border-gray-50 transition-all ${isEditing ? 'bg-primary-50' : 'hover:bg-gray-50'}`}>
                                                            <td className="px-3 py-2.5 text-sm font-medium text-gray-800">{r.ingredient_name}</td>
                                                            <td className="px-3 py-2.5 text-sm font-mono text-gray-600">{fmtRecipeQty(r.quantity, r.unit)}</td>
                                                            <td className="px-3 py-2.5 whitespace-nowrap">
                                                                {ing && unitPrice > 0 ? (
                                                                    <>
                                                                        {/* İtki varsa → birbaşa real (itkili) maya qırmızı; yoxsa adi mavi */}
                                                                        <div className={`text-sm font-mono font-semibold ${loss > 0 ? 'text-rose-500' : 'text-primary-600'}`}
                                                                            title={loss > 0 ? `Mədaxil itkisi ${trimNumber(loss * 100, 2)}% ilə real maya (itkisiz: ${lineCost.toFixed(2)} ₼)` : undefined}>
                                                                            {(loss > 0 ? effCost : lineCost).toFixed(2)} ₼
                                                                        </div>
                                                                        <div className="text-[10px] font-mono text-gray-400">{formatCostPerUnit(unitPrice, ing.unit)}</div>
                                                                        {loss > 0 && (
                                                                            <div className="text-[10px] font-mono font-semibold text-rose-500">
                                                                                itki ilə: {formatCostPerUnit(effUnit, ing.unit)}
                                                                            </div>
                                                                        )}
                                                                    </>
                                                                ) : <span className="text-gray-300">—</span>}
                                                            </td>
                                                            <td className="px-3 py-2.5 text-right">
                                                                <button
                                                                    onClick={() => {
                                                                        const eIng = ingredients.find(i => i.id === r.ingredient_id);
                                                                        setRecipeUnitMode('small'); // default qram/ml
                                                                        const disp = recipeToDisplay(r.quantity, eIng?.unit || r.unit, 'small');
                                                                        setRecipeForm({ ingredient_id: String(r.ingredient_id), quantity: String(trimNumber(disp)) });
                                                                        setRecipeSearch(r.ingredient_name);
                                                                    }}
                                                                    className="mr-1.5 text-gray-400 hover:text-primary-500 transition-colors text-sm"
                                                                    title="Düzəlt"
                                                                >✏️</button>
                                                                <button
                                                                    onClick={() => handleRecipeDelete(r.id)}
                                                                    className="text-gray-300 hover:text-red-500 transition-colors text-base font-bold leading-none"
                                                                    title="Sil"
                                                                >×</button>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                            ) : (
                                <div className="flex-1 flex items-center justify-center text-gray-400 text-sm p-8 text-center">
                                    Sol tərəfdən məhsul seçin
                                </div>
                            )}
                        </div>

                        {/* ── RIGHT: Ingredients palette ── */}
                        <div className={`lg:col-span-2 bg-white rounded-xl border border-gray-200 flex flex-col overflow-hidden ${panelH}`}>
                            <div className="px-4 py-3 border-b border-gray-100 flex-shrink-0">
                                <p className="font-bold text-gray-800 text-sm mb-2">🧪 İnqredientlər</p>
                                <input
                                    ref={recipeSearchRef}
                                    type="text"
                                    value={recipeSearch}
                                    onChange={e => {
                                        setRecipeSearch(e.target.value);
                                        setRecipeHighlight(-1);
                                        if (!e.target.value) setRecipeForm({ ingredient_id: '', quantity: '' });
                                    }}
                                    onKeyDown={e => {
                                        const list = filteredRightIngs;
                                        if (e.key === 'ArrowDown') {
                                            e.preventDefault();
                                            setRecipeHighlight(h => Math.min((h < 0 ? -1 : h) + 1, list.length - 1));
                                        } else if (e.key === 'ArrowUp') {
                                            e.preventDefault();
                                            setRecipeHighlight(h => Math.max(h - 1, 0));
                                        } else if (e.key === 'Enter') {
                                            e.preventDefault();
                                            if (list.length === 0 && recipeSearch.trim()) openIngCreateForRecipe(recipeSearch.trim());
                                            else if (list.length === 1) selectRecipeIng(list[0]);
                                            else if (recipeHighlight >= 0 && recipeHighlight < list.length) selectRecipeIng(list[recipeHighlight]);
                                            else if (list.length > 1) setRecipeHighlight(0);
                                        }
                                    }}
                                    placeholder="🔍 İnqredient axtar..."
                                    className="w-full px-3 py-2 border border-gray-200 rounded-xl text-xs focus:outline-none focus:border-primary-400 transition-all"
                                />
                            </div>

                            {/* Quick-add form when ingredient selected */}
                            {recipeForm.ingredient_id && selectedItem && (
                                <div className="px-3 py-2.5 border-b border-primary-100 bg-primary-50 flex-shrink-0">
                                    <div className="flex items-center justify-between gap-2 mb-1.5">
                                        <p className="text-[11px] font-semibold text-primary-700 truncate">➕ {selectedIngredient?.name}</p>
                                        {recSmallable ? (
                                            <span className="inline-flex rounded-md border border-primary-300 overflow-hidden text-[10px] font-bold shrink-0">
                                                {['small', 'base'].map(m => (
                                                    <button key={m} type="button"
                                                        onClick={() => {
                                                            const base = recipeToBase(recipeForm.quantity, selectedIngredient.unit, recMode);
                                                            setRecipeUnitMode(m);
                                                            setRecipeForm({ ...recipeForm, quantity: recipeForm.quantity === '' ? '' : String(trimNumber(recipeToDisplay(base, selectedIngredient.unit, m))) });
                                                        }}
                                                        className={`px-2 py-0.5 transition-all ${recMode === m ? 'bg-primary-500 text-white' : 'bg-white text-primary-600'}`}>
                                                        {m === 'small' ? (selectedIngredient.unit === 'kg' ? 'qram' : 'ml') : (selectedIngredient.unit === 'kg' ? 'kq' : 'lt')}
                                                    </button>
                                                ))}
                                            </span>
                                        ) : (
                                            <span className="text-primary-400 font-normal text-[11px] shrink-0">({unitLabel(selectedIngredient?.unit)})</span>
                                        )}
                                    </div>
                                    <form onSubmit={handleRecipeAdd} className="flex gap-1.5">
                                        <input
                                            ref={recipeQtyRef}
                                            type="number"
                                            value={recipeForm.quantity}
                                            onChange={e => setRecipeForm({ ...recipeForm, quantity: e.target.value })}
                                            placeholder={`Miqdar (${recDispUnit})`}
                                            step={recipeStep}
                                            min="0"
                                            autoFocus
                                            className="flex-1 px-2.5 py-1.5 border border-primary-200 rounded-lg text-xs focus:outline-none focus:border-primary-400 bg-white"
                                        />
                                        <button
                                            type="submit"
                                            className="px-3 py-1.5 rounded-lg text-xs font-bold text-white bg-primary-500 hover:bg-primary-600 transition-all"
                                        >
                                            Saxla
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => { setRecipeForm({ ingredient_id: '', quantity: '' }); setRecipeSearch(''); }}
                                            className="px-2 py-1.5 rounded-lg text-xs text-gray-500 border border-gray-200 hover:bg-gray-50 transition-all"
                                        >✕</button>
                                    </form>
                                    {/* Canlı önizləmə: miqdar × vahid qiymət = alış (baza vahidi ilə hesablanır) */}
                                    {recBaseQty > 0 && selectedIngredient && (() => {
                                        const up = Number(selectedIngredient.last_purchase_price ?? selectedIngredient.cost_per_unit) || 0;
                                        return up > 0 ? (
                                            <p className="mt-1.5 text-[11px] font-mono text-primary-700">
                                                {trimNumber(Number(recipeForm.quantity))} {recDispUnit} × {formatCostPerUnit(up, selectedIngredient.unit)} = <span className="font-bold">{(up * recBaseQty).toFixed(2)} ₼</span>
                                            </p>
                                        ) : null;
                                    })()}
                                </div>
                            )}

                            <div className="flex-1 overflow-y-auto p-2">
                                {filteredRightIngs.length === 0 ? (
                                    <div className="py-8 px-3 text-center">
                                        <p className="text-gray-400 text-xs mb-3">İnqredient tapılmadı</p>
                                        {recipeSearch.trim() && (
                                            <button
                                                type="button"
                                                onClick={() => openIngCreateForRecipe(recipeSearch.trim())}
                                                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold text-white bg-green-500 hover:bg-green-600 transition-all shadow-sm"
                                            >
                                                ➕ "{recipeSearch.trim()}" əlavə et
                                            </button>
                                        )}
                                        <p className="mt-2 text-[10px] text-gray-400">Enter düyməsi də yeni inqredient pəncərəsini açır</p>
                                    </div>
                                ) : filteredRightIngs.map((ing, idx) => {
                                    const isInRecipe = recipes.some(r => r.ingredient_id === ing.id);
                                    const isSelected = String(ing.id) === String(recipeForm.ingredient_id);
                                    const isHighlighted = idx === recipeHighlight;
                                    return (
                                        <div
                                            key={ing.id}
                                            onClick={() => selectRecipeIng(ing)}
                                            className={`flex items-center justify-between px-3 py-2 rounded-lg cursor-pointer transition-all mb-0.5 ${
                                                isHighlighted
                                                    ? 'bg-primary-100 border border-primary-400 text-primary-800 ring-1 ring-primary-300'
                                                    : isSelected
                                                    ? 'bg-primary-50 border border-primary-200 text-primary-700'
                                                    : isInRecipe
                                                        ? 'bg-green-50 border border-green-100 text-green-800 hover:bg-green-100'
                                                        : 'hover:bg-gray-50 text-gray-700 border border-transparent'
                                            }`}
                                        >
                                            <div className="min-w-0">
                                                <p className="text-sm font-semibold truncate">{ing.name}</p>
                                                <p className="text-[11px] text-gray-400 mt-0.5">
                                                    {unitLabel(ing.unit)}
                                                    <span className="mx-1">·</span>
                                                    <span className="font-mono">{formatCostPerUnit(ing.last_purchase_price ?? ing.cost_per_unit, ing.unit)}</span>
                                                </p>
                                            </div>
                                            {isInRecipe && !isSelected && (
                                                <span className="text-[10px] font-bold text-green-600 bg-green-100 rounded-full px-2 py-0.5 shrink-0 ml-1">✓</span>
                                            )}
                                            {isSelected && (
                                                <span className="text-[10px] font-bold text-primary-600 bg-primary-100 rounded-full px-2 py-0.5 shrink-0 ml-1">✏️</span>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                    </div>
                    </>
                    );
                })()}

                {/* ── HƏRƏKƏT TARİXİ ── */}
                {tab === 'history' && (
                    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                        {/* İnqredient seç */}
                        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                            <div className="px-4 py-3 border-b border-gray-100">
                                <h3 className="font-semibold text-gray-700 text-sm">İnqredient seç</h3>
                            </div>
                            <div className="px-3 pt-3">
                                <input
                                    type="text"
                                    value={historySearch}
                                    onChange={(e) => setHistorySearch(e.target.value)}
                                    placeholder="🔍 İnqredient axtar..."
                                    className={inputClass}
                                />
                            </div>
                            <div className="p-2 max-h-96 overflow-y-auto">
                                {filteredHistoryIngredients.map(ing => (
                                    <div
                                        key={ing.id}
                                        onClick={() => loadMovements(ing.id)}
                                        className={`px-3 py-2 rounded-lg cursor-pointer text-sm transition-all mb-0.5 ${
                                            selectedIngId === ing.id
                                                ? 'bg-primary-50 text-primary-700 font-semibold'
                                                : 'hover:bg-gray-50 text-gray-600'
                                        }`}
                                    >
                                        {ing.name}
                                        {ing.low_stock ? ' ⚠️' : ''}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Hərəkətlər */}
                        <div className="lg:col-span-3 bg-white rounded-xl border border-gray-200 overflow-hidden">
                            <div className="px-5 py-4 border-b border-gray-100">
                                <h3 className="font-bold text-gray-800">📊 Hərəkət tarixi</h3>
                            </div>
                            {movements.length === 0 ? (
                                <div className="p-8 text-center text-gray-400 text-sm">
                                    {selectedIngId ? 'Hərəkət yoxdur.' : 'Sol tərəfdən inqredient seçin.'}
                                </div>
                            ) : (
                                <table className="w-full">
                                    <thead>
                                        <tr className="bg-gray-50">
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Tarix</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Növ</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Hərəkət</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Miqdar</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Qalıq</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">Qeyd</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase">İstifadəçi</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {movementRows.map(m => (
                                            <tr key={m.id} className="border-t border-gray-50 hover:bg-gray-50">
                                                <td className="px-4 py-3 text-xs text-gray-400 font-mono">
                                                    {fmtDateTime(m.created_at)}
                                                </td>
                                                <td className="px-4 py-3">
                                                    <span className={`text-xs font-bold px-2 py-1 rounded-full ${
                                                        m.type === 'giris'    ? 'bg-green-100 text-green-700' :
                                                        m.type === 'cixis'    ? 'bg-red-100 text-red-600'    :
                                                                                'bg-blue-100 text-blue-600'
                                                    }`}>
                                                        {m.type === 'giris'    ? '📥 Giriş'   :
                                                         m.type === 'cixis'    ? '📤 Çıxış'   :
                                                                                 '✏️ Düzəltmə'}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 text-xs font-mono text-gray-500">
                                                    {m.change_label}
                                                </td>
                                                <td className="px-4 py-3 font-bold font-mono text-sm">
                                                    {formatByUnit(m.quantity, movementUnit)}
                                                </td>
                                                <td className="px-4 py-3 font-bold font-mono text-sm text-primary-600">
                                                    {formatByUnit(m.balance_after, movementUnit)}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-500">
                                                    {m.note || (m.order_num ? `Sifariş #${m.order_num}` : '—')}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-500">
                                                    {m.user_name || '—'}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                )}

                {tab === 'menu' && (
                    <AdminMenu embedded />
                )}

                {/* ── SAYIM ── */}
                {tab === 'sayim' && (
                    <div>
                        {/* Toolbar — mobildə yan-sürüşən sıra, desktopda wrap */}
                        <div className="flex md:flex-wrap items-center gap-2 mb-4 overflow-x-auto md:overflow-visible pb-2 md:pb-0 [&>button]:shrink-0 [&>button]:whitespace-nowrap">
                            <button onClick={loadSayim}
                                className="px-3 py-2 rounded-xl text-xs font-bold bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 transition-all">
                                🔄 Yenilə
                            </button>
                            {(isAdmin || user?.role === 'anbardar') && (
                                <button onClick={() => { setPrefForm({ menu_item_id: '', variant_id: '', quantity: '', note: '' }); setPrefVariants([]); setPrefModal(true); }}
                                    className="px-3 py-2 rounded-xl text-xs font-bold bg-purple-50 border border-purple-200 text-purple-700 hover:bg-purple-100 transition-all">
                                    ⚙️ Prefaktoriya
                                </button>
                            )}
                            {isAdmin && (<>
                                <button onClick={() => {
                                    const now = new Date();
                                    const local = new Date(now - now.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
                                    setAllDateForm({ sayim_date: local, note: '' });
                                    setAllDateModal(true);
                                }}
                                    className="px-3 py-2 rounded-xl text-xs font-bold bg-indigo-50 border border-indigo-200 text-indigo-700 hover:bg-indigo-100 transition-all">
                                    📅 Keçən sayım tarixi
                                </button>
                                <button onClick={() => { loadHistory(); setHistoryModal(true); }}
                                    className="px-3 py-2 rounded-xl text-xs font-bold bg-sky-50 border border-sky-200 text-sky-700 hover:bg-sky-100 transition-all">
                                    🕐 Tarixçə / Bərpa
                                </button>

                                <button onClick={() => { setEditDepot(null); setDepotForm({ name: '' }); setDepotModal(true); }}
                                    className="px-3 py-2 rounded-xl text-xs font-bold bg-orange-50 border border-orange-200 text-orange-700 hover:bg-orange-100 transition-all">
                                    🏪 Depolar
                                </button>
                                <button onClick={() => openPrintSelect(null)}
                                    className="px-3 py-2 rounded-xl text-xs font-bold bg-slate-50 border border-slate-200 text-slate-700 hover:bg-slate-100 transition-all">
                                    🖨️ Çap et
                                </button>
                                <button onClick={printBlankCountSheet}
                                    className="px-3 py-2 rounded-xl text-xs font-bold bg-amber-50 border border-amber-200 text-amber-700 hover:bg-amber-100 transition-all">
                                    📄 Sayım vərəqi
                                </button>
                                <button onClick={() => setRecipeModal(true)}
                                    className="px-3 py-2 rounded-xl text-xs font-bold bg-indigo-50 border border-indigo-200 text-indigo-700 hover:bg-indigo-100 transition-all">
                                    📋 Texkarta çap
                                </button>
                                <button onClick={openYeniSayim}
                                    className="px-3 py-2 rounded-xl text-xs font-bold bg-teal-50 border border-teal-200 text-teal-700 hover:bg-teal-100 transition-all">
                                    📋 Yeni Sayım
                                </button>
                                {/* Aktiv sessiya yoxdursa admin özü başlada bilər */}
                                {sayimActiveSessions.length === 0 && (
                                    <button onClick={startAdminSayim}
                                        className="px-3 py-2 rounded-xl text-xs font-bold bg-blue-50 border border-blue-200 text-blue-700 hover:bg-blue-100 transition-all md:ml-auto">
                                        ▶️ Sayıma başla
                                    </button>
                                )}
                                {/* "Sayımı Bitir" yalnız aktiv sayım sessiyası olanda işləyir — təsadüfi bütün-depo sıfırlamanın qarşısı */}
                                <button onClick={() => setBitirModal(true)}
                                    disabled={sayimActiveSessions.length === 0}
                                    title={sayimActiveSessions.length === 0 ? 'Əvvəlcə "Sayıma başla" ilə sayım başladın' : 'Sayımı bitir'}
                                    className={`px-3 py-2 rounded-xl text-xs font-bold border transition-all ${sayimActiveSessions.length > 0 ? 'md:ml-auto' : ''} ${
                                        sayimActiveSessions.length === 0
                                            ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
                                            : 'bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100'
                                    }`}>
                                    ✅ Sayımı Bitir
                                </button>
                            </>)}
                        </div>

                        {/* Kassir sayım sessiyaları — PARALEL: hər gözləyən/aktiv anbar ayrıca (yalnız admin) */}
                        {isAdmin && sayimRequested.map(s => (
                            <div key={`req-${s.depot_id}`} className="mb-2 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3">
                                <div className="text-sm font-semibold text-amber-800">
                                    🔔 Kassir «<span className="font-black">{depLabelOf(s)}</span>» üçün sayım icazəsi istəyir.
                                </div>
                                <div className="flex gap-2">
                                    <button onClick={() => approveSayim(s.depot_id)}
                                        className="rounded-lg bg-emerald-600 px-4 py-1.5 text-xs font-bold text-white hover:bg-emerald-700">✅ Təsdiqlə</button>
                                    <button onClick={() => rejectSayim(s.depot_id)}
                                        className="rounded-lg border border-red-200 bg-white px-4 py-1.5 text-xs font-bold text-red-600 hover:bg-red-50">✕ İmtina</button>
                                </div>
                            </div>
                        ))}
                        {isAdmin && sayimActiveSessions.length > 0 && (
                            <div className="mb-4 flex flex-wrap items-center gap-3 rounded-xl border border-emerald-300 bg-emerald-50 px-4 py-3">
                                <span className="text-sm font-semibold text-emerald-800">🟢 Aktiv sayım — anbar seç:</span>
                                <select value={sayimSelDepot ?? ''} onChange={e => setSayimSelDepot(Number(e.target.value))}
                                    className="rounded-lg border-2 border-emerald-200 bg-white px-3 py-1.5 text-sm font-bold text-emerald-800 focus:outline-none">
                                    {sayimActiveSessions.map(s => (
                                        <option key={s.depot_id} value={Number(s.depot_id)}>🏪 {depLabelOf(s)}</option>
                                    ))}
                                </select>
                                <span className="text-xs text-emerald-600">— "Yeni Sayım" / "Sayımı Bitir" bu anbara aiddir.</span>
                                <button onClick={() => rejectSayim(sayimSelDepot)}
                                    className="ml-auto rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-bold text-red-600 hover:bg-red-50">
                                    Bu anbarı ləğv et
                                </button>
                            </div>
                        )}

                        {/* Formula hint */}
                        <div className="mb-3 px-3 py-2 bg-blue-50 border border-blue-100 rounded-xl text-[11px] text-blue-700 font-mono">
                            Qalıq = Keçən sayım + Mədaxil − Məd.itkisi − İstifadə − İtki − Tərg − Prefaktoriya
                        </div>

                        {/* Axtarış */}
                        <div className="relative mb-3">
                            <input
                                type="text"
                                value={sayimSearch}
                                onChange={(e) => setSayimSearch(e.target.value)}
                                placeholder="🔍 İnqredient adı və ya depo üzrə axtar..."
                                className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 pr-9 text-sm focus:outline-none focus:border-primary-400"
                            />
                            {sayimSearch && (
                                <button
                                    type="button"
                                    onClick={() => setSayimSearch('')}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 text-xs"
                                >
                                    ✕
                                </button>
                            )}
                        </div>

                        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                            <div className="overflow-x-auto">
                                {sayimLoading ? (
                                    <div className="py-12 text-center text-gray-400">Yüklənir...</div>
                                ) : sayimData.length === 0 ? (
                                    <div className="py-12 text-center text-gray-400">İnqredient tapılmadı.</div>
                                ) : (
                                    <table className="w-full min-w-[1000px]">
                                        <thead>
                                            <tr className="bg-gray-50 border-b border-gray-100">
                                                <th className="px-3 py-3 text-left text-[11px] font-bold text-gray-500 uppercase">Ad</th>
                                                <th className="px-3 py-3 text-left text-[11px] font-bold text-gray-500 uppercase whitespace-nowrap">Keçən sayım</th>
                                                <th className="px-3 py-3 text-right text-[11px] font-bold text-green-600 uppercase whitespace-nowrap">Mədaxil</th>
                                                <th className="px-3 py-3 text-right text-[11px] font-bold text-orange-500 uppercase whitespace-nowrap">Məd.itkisi</th>
                                                <th className="px-3 py-3 text-right text-[11px] font-bold text-gray-500 uppercase whitespace-nowrap">İstifadə</th>
                                                <th className="px-3 py-3 text-right text-[11px] font-bold text-red-500 uppercase whitespace-nowrap">İtki</th>
                                                <th className="px-3 py-3 text-right text-[11px] font-bold text-amber-600 uppercase whitespace-nowrap">Tərg</th>
                                                <th className="px-3 py-3 text-right text-[11px] font-bold text-purple-600 uppercase whitespace-nowrap">Prefakt.</th>
                                                <th className="px-3 py-3 text-right text-[11px] font-bold text-primary-700 uppercase whitespace-nowrap">Qalıq</th>
                                                <th className="px-3 py-3 text-left text-[11px] font-bold text-gray-400 uppercase">Əməl.</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(() => {
                                                // Filter by search
                                                const q = sayimSearch.toLowerCase().trim();
                                                const filteredData = !q ? sayimData : sayimData.filter(r =>
                                                    String(r.name || '').toLowerCase().includes(q) ||
                                                    String(r.depot_name || '').toLowerCase().includes(q)
                                                );
                                                // Group filteredData by depot
                                                const groups = [];
                                                let lastDepot = undefined;
                                                filteredData.forEach(row => {
                                                    if (row.depot_name !== lastDepot) {
                                                        groups.push({ label: row.depot_name, rows: [] });
                                                        lastDepot = row.depot_name;
                                                    }
                                                    groups[groups.length - 1].rows.push(row);
                                                });
                                                return groups.map(g => (
                                                    <>
                                                        {g.label && (
                                                            <tr key={`depot-${g.label}`}>
                                                                <td colSpan={10} className="px-3 py-2 bg-slate-800 text-white text-xs font-bold tracking-wide">
                                                                    🏪 {g.label}
                                                                </td>
                                                            </tr>
                                                        )}
                                                        {g.rows.map(row => {
                                                const fmt = (v) => formatByUnit(v, row.unit);
                                                const isNeg = row.qaliq < 0;
                                                return (
                                                    <tr key={row.id} className="border-t border-gray-50 hover:bg-gray-50">
                                                        {/* Ad */}
                                                        <td className="px-3 py-2.5 text-sm font-semibold text-gray-800 whitespace-nowrap max-w-[130px] truncate">
                                                            {row.name}
                                                            {row.mexaric_itki_percent > 0 && (
                                                                <span className="ml-1 text-[10px] text-red-400 font-normal">
                                                                    -{row.mexaric_itki_percent}{(row.unit === 'gram' || row.unit === 'ml') ? '/kq' : '/vahid'}
                                                                </span>
                                                            )}
                                                        </td>

                                                        {/* Keçən sayım */}
                                                        <td className="px-3 py-2.5 min-w-[140px]">
                                                            {sayimEditId === row.id ? (
                                                                <div className="flex flex-col gap-1">
                                                                    <input type="number" value={sayimEditForm.amount}
                                                                        onChange={e => setSayimEditForm(f => ({ ...f, amount: e.target.value }))}
                                                                        className="w-full px-2 py-1 border border-primary-300 rounded-lg text-xs font-mono focus:outline-none"
                                                                        step="0.001" placeholder="Miqdar" autoFocus />
                                                                    <div className="flex gap-1">
                                                                        <button onClick={() => handleSaveSayimEdit(row.id)}
                                                                            className="flex-1 px-2 py-1 rounded-lg text-[10px] font-bold bg-primary-500 text-white hover:bg-primary-600">
                                                                            Saxla
                                                                        </button>
                                                                        <button onClick={() => setSayimEditId(null)}
                                                                            className="px-2 py-1 rounded-lg text-[10px] text-gray-500 border border-gray-200 hover:bg-gray-50">
                                                                            ✕
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            ) : (
                                                                <div>
                                                                    <p className="font-mono font-bold text-sm text-gray-800">{fmt(row.kecan_sayim)}</p>
                                                                    {row.sayim_date && (
                                                                        <p className="text-[10px] text-gray-400 font-mono mt-0.5">{fmtDateTime(row.sayim_date)}</p>
                                                                    )}
                                                                    {isAdmin && (
                                                                        <button onClick={() => openSayimEdit(row)}
                                                                            className="text-[10px] text-primary-500 hover:underline mt-0.5">
                                                                            ✏️ Dəyiş
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </td>

                                                        {/* Mədaxil */}
                                                        <td className="px-3 py-2.5 text-right font-mono font-bold text-sm text-green-600">
                                                            {row.medaxil > 0 ? fmt(row.medaxil) : <span className="text-gray-300">0</span>}
                                                        </td>

                                                        {/* Mədaxil itkisi (+ tənzimlənən faiz — məlumat üçün) */}
                                                        <td className="px-3 py-2.5 text-right font-mono text-sm text-orange-500">
                                                            {row.medaxil_itki > 0 ? fmt(row.medaxil_itki) : <span className="text-gray-300">0</span>}
                                                            {medItkiRate(row.unit, row.medaxil_itki_percent) && (
                                                                <span className="block text-[10px] font-sans text-gray-400">{medItkiRate(row.unit, row.medaxil_itki_percent)}</span>
                                                            )}
                                                        </td>

                                                        {/* İstifadə */}
                                                        <td className="px-3 py-2.5 text-right font-mono text-sm text-gray-600">
                                                            {row.istifade > 0 ? fmt(row.istifade) : <span className="text-gray-300">0</span>}
                                                        </td>

                                                        {/* İtki (mexaric) */}
                                                        <td className="px-3 py-2.5 text-right font-mono text-sm text-red-500">
                                                            {row.itki > 0 ? fmt(row.itki) : <span className="text-gray-300">0</span>}
                                                        </td>

                                                        {/* Tərg */}
                                                        <td className="px-3 py-2.5 text-right font-mono text-sm text-amber-600">
                                                            {row.terg > 0 ? fmt(row.terg) : <span className="text-gray-300">0</span>}
                                                        </td>

                                                        {/* Prefaktoriya */}
                                                        <td className="px-3 py-2.5 text-right font-mono text-sm text-purple-600">
                                                            {row.prefaktoriya > 0 ? fmt(row.prefaktoriya) : <span className="text-gray-300">0</span>}
                                                        </td>

                                                        {/* Qalıq */}
                                                        <td className={`px-3 py-2.5 text-right font-mono font-bold text-sm ${isNeg ? 'text-red-600' : 'text-primary-700'}`}>
                                                            <span className={`px-2 py-0.5 rounded-lg ${isNeg ? 'bg-red-50' : 'bg-primary-50'}`}>
                                                                {fmt(row.qaliq)}
                                                            </span>
                                                        </td>

                                                        {/* Actions */}
                                                        <td className="px-3 py-2.5">
                                                            <button onClick={() => { setTergModal({ id: row.id, name: row.name, unit: row.unit }); setTergForm({ amount: '', note: '' }); }}
                                                                className="px-2 py-1 rounded-lg text-[10px] font-semibold bg-amber-50 text-amber-700 hover:bg-amber-100 whitespace-nowrap"
                                                                title="Tərg əlavə et">
                                                                Tərg
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                                        })}
                                                    </>
                                                ));
                                            })()}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* ── TƏRG MODAL ── */}
            {tergModal && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
                     onClick={e => e.target === e.currentTarget && setTergModal(null)}>
                    <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl">
                        <h3 className="font-bold text-lg mb-1 text-gray-800">Tərg et</h3>
                        <p className="text-sm text-gray-500 mb-4">{tergModal.name}</p>
                        <form onSubmit={handleAddTerg} className="flex flex-col gap-3">
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1.5">Miqdar ({unitLabel(tergModal.unit)})</label>
                                <input type="number" step="0.001" min="0.001" required
                                    value={tergForm.amount}
                                    onChange={e => setTergForm(f => ({ ...f, amount: e.target.value }))}
                                    className={inputClass} autoFocus />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1.5">Səbəb / Qeyd</label>
                                <input type="text" value={tergForm.note}
                                    onChange={e => setTergForm(f => ({ ...f, note: e.target.value }))}
                                    placeholder="Məs: Xarab olub" className={inputClass} />
                            </div>
                            <div className="flex gap-3 mt-1">
                                <button type="button" onClick={() => setTergModal(null)}
                                    className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-500">Ləğv et</button>
                                <button type="submit"
                                    className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white bg-amber-500 hover:bg-amber-600">Tərg et</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* ── PREFAKTORİYA MODAL ── */}
            {prefModal && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
                     onClick={e => e.target === e.currentTarget && setPrefModal(false)}>
                    <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl">
                        <h3 className="font-bold text-lg mb-1 text-gray-800">Prefaktoriya</h3>
                        <p className="text-xs text-gray-400 mb-4">Daxili istehsal: məhsul seçin, neçə ədəd hazırlandığını yazın. Texkarta üzrə inqredientlər düşülür.</p>
                        <form onSubmit={handleAddPref} className="flex flex-col gap-3">
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1.5">Məhsul</label>
                                <SearchSelect
                                    value={prefForm.menu_item_id}
                                    onChange={async (v) => {
                                        setPrefForm(f => ({ ...f, menu_item_id: v, variant_id: '' }));
                                        // Load variants
                                        try {
                                            const res = await api.get(`/menu/items/${v}/variants`);
                                            setPrefVariants(res.data.data || []);
                                        } catch { setPrefVariants([]); }
                                    }}
                                    options={menuItems.map(i => ({ value: String(i.id), label: i.name }))}
                                    placeholder="Məhsul seç..."
                                />
                            </div>
                            {prefVariants.length > 0 && (
                                <div>
                                    <label className="block text-xs font-semibold text-gray-500 uppercase mb-1.5">Variant</label>
                                    <SearchSelect
                                        value={prefForm.variant_id}
                                        onChange={v => setPrefForm(f => ({ ...f, variant_id: v }))}
                                        options={[
                                            { value: '', label: 'Ortaq texkarta (bütün variantlar)' },
                                            ...prefVariants.map(v => ({ value: String(v.id), label: v.name }))
                                        ]}
                                        placeholder="Variant seç..."
                                    />
                                </div>
                            )}
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1.5">Miqdar (neçə ədəd hazırlandı)</label>
                                <input type="number" step="0.001" min="0.001" required
                                    value={prefForm.quantity}
                                    onChange={e => setPrefForm(f => ({ ...f, quantity: e.target.value }))}
                                    className={inputClass} />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1.5">Qeyd</label>
                                <input type="text" value={prefForm.note}
                                    onChange={e => setPrefForm(f => ({ ...f, note: e.target.value }))}
                                    placeholder="İstəyə bağlı" className={inputClass} />
                            </div>
                            <div className="flex gap-3 mt-1">
                                <button type="button" onClick={() => setPrefModal(false)}
                                    className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-500">Ləğv et</button>
                                <button type="submit"
                                    className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white bg-purple-600 hover:bg-purple-700">Qeyd et</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* ── SAYIMI BİTİR MODAL ── */}
            {bitirModal && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
                     onClick={e => e.target === e.currentTarget && setBitirModal(false)}>
                    <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl">
                        <h3 className="font-bold text-lg mb-2 text-gray-800">✅ Sayımı Bitir</h3>
                        {Number(sayimSession.depot_id) > 0 ? (
                            <p className="text-sm font-bold text-teal-700 bg-teal-50 border border-teal-200 rounded-xl px-3 py-2 mb-2">
                                🏪 Yalnız «{sayimSession.depot_name}» anbarı bitiriləcək — digər anbarlar toxunulmayacaq.
                            </p>
                        ) : (
                            <p className="text-sm font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 mb-2">
                                🏬 Bütün depolar bitiriləcək.
                            </p>
                        )}
                        <p className="text-sm text-gray-500 mb-1">
                            <strong>Yeni Sayım</strong>-da daxil edilmiş rəqəmlər yeni <strong>Keçən sayım</strong> olacaq.
                            Sayım tarixi cari Bakı vaxtı ilə yazılacaq.
                        </p>
                        <p className="text-xs text-blue-600 bg-blue-50 border border-blue-100 rounded-xl px-3 py-2 mb-4">
                            Kassirin boş buraxdığı məhsullar 0 kimi keçəcək.
                            {Number(sayimSession.depot_id) > 0 ? ' Bu anbarın tərg və mədaxil itkisi sıfırlanacaq.' : ' Tərg, mədaxil itkisi və prefaktoriya sıfırlanacaq.'}
                        </p>
                        <div className="mb-4">
                            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1.5">Qeyd (istəyə bağlı)</label>
                            <input type="text" value={bitirNote}
                                onChange={e => setBitirNote(e.target.value)}
                                placeholder="Məs: May ayı sayımı" className={inputClass} />
                        </div>
                        <div className="flex gap-3">
                            <button type="button" onClick={() => setBitirModal(false)}
                                className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-500">Ləğv et</button>
                            <button type="button" onClick={handleBitir} disabled={bitirLoading}
                                className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50">
                                {bitirLoading ? 'Yazılır...' : '✅ Bitir'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── HAMISI ÜÇÜN TARİX MODAL ── */}
            {allDateModal && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
                     onClick={e => e.target === e.currentTarget && setAllDateModal(false)}>
                    <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl">
                        <h3 className="font-bold text-lg mb-1 text-gray-800">📅 Keçən sayım tarixi</h3>
                        <p className="text-xs text-gray-400 mb-4">Seçilmiş tarix/saat bütün inqredientlərə tətbiq olunacaq. Mövcud miqdarlar dəyişməz.</p>
                        <form onSubmit={handleSetAllDate} className="flex flex-col gap-3">
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1.5">Sayım tarixi və saatı</label>
                                <input type="datetime-local" required
                                    value={allDateForm.sayim_date}
                                    onChange={e => setAllDateForm(f => ({ ...f, sayim_date: e.target.value }))}
                                    className={inputClass} autoFocus />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1.5">Qeyd (istəyə bağlı)</label>
                                <input type="text" value={allDateForm.note}
                                    onChange={e => setAllDateForm(f => ({ ...f, note: e.target.value }))}
                                    placeholder="Məs: Yanvar sayımı" className={inputClass} />
                            </div>
                            <div className="flex gap-3 mt-1">
                                <button type="button" onClick={() => setAllDateModal(false)}
                                    className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-500">Ləğv et</button>
                                <button type="submit" disabled={allDateLoading}
                                    className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50">
                                    {allDateLoading ? 'Tətbiq edilir...' : 'Hamısına tətbiq et'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* ── TARİXÇƏ / BƏRPA MODAL ── */}
            {historyModal && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
                     onClick={e => e.target === e.currentTarget && setHistoryModal(false)}>
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg">
                        <div className="border-b border-gray-100 px-5 py-4 flex items-center justify-between">
                            <h3 className="font-bold text-gray-800">🕐 Sayım tarixçəsi</h3>
                            <button onClick={() => setHistoryModal(false)} className="text-gray-400 hover:text-gray-600 text-xl leading-none">✕</button>
                        </div>
                        <div className="p-4 max-h-96 overflow-y-auto">
                            {historyLoading ? (
                                <div className="py-8 text-center text-gray-400">Yüklənir...</div>
                            ) : historyData.length === 0 ? (
                                <div className="py-8 text-center text-gray-400">Tarixçə yoxdur.</div>
                            ) : historyData.map(h => (
                                <div key={h.id} className="flex items-center justify-between px-3 py-3 rounded-xl border border-gray-100 mb-2 hover:bg-gray-50">
                                    <div>
                                        <p className="text-sm font-semibold text-gray-800">{fmtDateTime(h.reset_at)}</p>
                                        <p className="text-xs text-gray-400 mt-0.5">{h.note || '—'} · {h.reset_by_name || '?'}</p>
                                        {kassirCountOf(h) > 0 && (
                                            <p className="text-[11px] font-semibold text-emerald-600 mt-0.5">
                                                📝 {kassirCountOf(h)} kassir sayımı — bərpada "Sayılan"a gələcək
                                            </p>
                                        )}
                                    </div>
                                    <button onClick={() => handleRestore(h.id)}
                                        className="px-3 py-1.5 rounded-lg text-xs font-bold bg-sky-50 text-sky-600 border border-sky-200 hover:bg-sky-100 shrink-0">
                                        Bərpa et
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* ── YENİ SAYIM MODAL ── */}
            {yeniSayimModal && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[96vw] flex flex-col" style={{ maxHeight: '90vh' }}>

                        {/* Header */}
                        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
                            <div>
                                <h3 className="font-bold text-lg text-gray-800">
                                    {yeniSayimResult ? '📊 Sayım Nəticəsi' : '📋 Yeni Sayım'}
                                </h3>
                                <p className="text-xs text-gray-400 mt-0.5">
                                    {sayimSession.depot_name && <span className="font-bold text-teal-600">🏪 {sayimSession.depot_name} · </span>}
                                    {yeniSayimResult
                                        ? 'Sayılan vs. Gözlənilən müqayisəsi'
                                        : 'Hər məhsul üçün sayılan miqdarı daxil edin'}
                                </p>
                            </div>
                            <button onClick={() => setYeniSayimModal(false)}
                                className="w-8 h-8 flex items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 text-xl font-bold leading-none">
                                ×
                            </button>
                        </div>

                        {/* ── INPUT VIEW ── */}
                        {!yeniSayimResult && (<>
                            <div className="px-6 py-3 border-b border-gray-100 flex-shrink-0 flex items-center gap-3">
                                <input
                                    type="text"
                                    value={yeniSayimSearch}
                                    onChange={e => setYeniSayimSearch(e.target.value)}
                                    placeholder="🔍 Məhsul axtar..."
                                    className="flex-1 px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary-400 transition-all"
                                />
                                <label className="flex items-center gap-2 cursor-pointer select-none whitespace-nowrap">
                                    <input
                                        type="checkbox"
                                        checked={yeniSayimBoşOnly}
                                        onChange={e => setYeniSayimBoşOnly(e.target.checked)}
                                        className="w-4 h-4 rounded border-gray-300 text-primary-600 cursor-pointer"
                                    />
                                    <span className="text-xs font-semibold text-gray-500">Yalnız boş</span>
                                </label>
                            </div>
                            <div className="flex-1 overflow-y-auto">
                                <table className="w-full">
                                    <thead className="sticky top-0 bg-gray-50 z-10">
                                        <tr>
                                            <th className="px-4 py-3 text-left text-[11px] font-bold text-gray-400 uppercase">Məhsul</th>
                                            <th className="px-4 py-3 text-left text-[11px] font-bold text-gray-400 uppercase">Vahid</th>
                                            <th className="px-4 py-3 text-left text-[11px] font-bold text-indigo-600 uppercase w-52">Sayılan miqdar</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(() => {
                                            const ulMap = { gram: 'q', ml: 'ml', eded: 'əd', kg: 'kq', litr: 'lt' };
                                            const filtered = yeniSayimData.filter(r => {
                                                if (yeniSayimSearch && !r.name.toLowerCase().includes(yeniSayimSearch.toLowerCase())) return false;
                                                if (yeniSayimBoşOnly && yeniSayimInputs[r.id] !== undefined && yeniSayimInputs[r.id] !== '') return false;
                                                return true;
                                            });
                                            // Group by depot
                                            const groups2 = [];
                                            let lastDep = undefined;
                                            filtered.forEach(row => {
                                                if (row.depot_name !== lastDep) {
                                                    groups2.push({ label: row.depot_name, rows: [] });
                                                    lastDep = row.depot_name;
                                                }
                                                groups2[groups2.length - 1].rows.push(row);
                                            });
                                            return groups2.map(g => (
                                                <>
                                                    {g.label && (
                                                        <tr key={`dep-${g.label}`}>
                                                            <td colSpan={3} className="px-4 py-1.5 bg-slate-800 text-white text-xs font-bold">🏪 {g.label}</td>
                                                        </tr>
                                                    )}
                                                    {g.rows.map(row => {
                                                        const ul        = ulMap[row.unit] || row.unit;
                                                        const step      = row.unit === 'eded' ? '1' : '0.001';
                                                        const hasFilled = yeniSayimInputs[row.id] !== undefined && yeniSayimInputs[row.id] !== '';
                                                        return (
                                                            <tr key={row.id} className={`border-t border-gray-50 transition-colors ${hasFilled ? 'bg-teal-50' : 'hover:bg-gray-50'}`}>
                                                                <td className="px-4 py-2.5 font-semibold text-sm text-gray-800">{row.name}</td>
                                                                <td className="px-4 py-2.5 text-sm text-gray-400">{ul}</td>
                                                                <td className="px-4 py-2">
                                                                    <input
                                                                        type="number"
                                                                        min="0"
                                                                        step={step}
                                                                        value={yeniSayimInputs[row.id] ?? ''}
                                                                        onChange={e => {
                                                                            const updated = { ...yeniSayimInputs, [row.id]: e.target.value };
                                                                            setYeniSayimInputs(updated);
                                                                            saveYeniSayimToStorage(updated);
                                                                            syncDraftToServer(updated);
                                                                        }}
                                                                        placeholder={sayimActive ? '0' : 'Miqdar...'}
                                                                        className="w-full px-3 py-1.5 border-2 border-gray-200 rounded-xl text-sm font-mono focus:outline-none focus:border-indigo-400 transition-all"
                                                                    />
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </>
                                            ));
                                        })()}
                                    </tbody>
                                </table>
                            </div>
                            <div className="px-6 py-4 border-t border-gray-100 flex-shrink-0 flex items-center justify-between">
                                <p className="text-xs text-gray-400">
                                    <span className="font-semibold text-teal-600">
                                        {yeniSayimData.filter(r => yeniSayimInputs[r.id] !== undefined && yeniSayimInputs[r.id] !== '').length}
                                    </span> / {yeniSayimData.length} məhsul daxil edilib
                                </p>
                                <div className="flex gap-3">
                                    <button onClick={() => setYeniSayimModal(false)}
                                        className="px-5 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-500 hover:bg-gray-50">
                                        Ləğv et
                                    </button>
                                    <button onClick={handleYeniSayimTamamla}
                                        className="px-5 py-2.5 rounded-xl text-sm font-bold text-white"
                                        style={{ background: 'linear-gradient(135deg, #006f91, #0090bb)' }}>
                                        Tamamla →
                                    </button>
                                </div>
                            </div>
                        </>)}

                        {/* ── RESULT VIEW ── */}
                        {yeniSayimResult && (<>
                            <div className="flex-1 overflow-auto">
                                <table className="w-full min-w-[720px]">
                                    <thead className="sticky top-0 bg-gray-50 z-10">
                                        <tr>
                                            <th className="px-3 py-3 text-left text-[11px] font-bold text-gray-500 uppercase">Ad</th>
                                            <th className="px-3 py-3 text-right text-[11px] font-bold text-gray-500 uppercase whitespace-nowrap">Keçən sayım</th>
                                            <th className="px-3 py-3 text-right text-[11px] font-bold text-green-600 uppercase whitespace-nowrap">Mədaxil</th>
                                            <th className="px-3 py-3 text-right text-[11px] font-bold text-orange-500 uppercase whitespace-nowrap">Məd.itkisi</th>
                                            <th className="px-3 py-3 text-right text-[11px] font-bold text-gray-500 uppercase whitespace-nowrap">İstifadə</th>
                                            <th className="px-3 py-3 text-right text-[11px] font-bold text-red-500 uppercase whitespace-nowrap">İtki</th>
                                            <th className="px-3 py-3 text-right text-[11px] font-bold text-amber-600 uppercase whitespace-nowrap">Tərg</th>
                                            <th className="px-3 py-3 text-right text-[11px] font-bold text-purple-600 uppercase whitespace-nowrap">Prefakt.</th>
                                            <th className="px-3 py-3 text-right text-[11px] font-bold text-primary-700 uppercase whitespace-nowrap">Qalıq</th>
                                            <th className="px-3 py-3 text-right text-[11px] font-bold text-indigo-600 uppercase whitespace-nowrap">Sayılan</th>
                                            <th className="px-3 py-3 text-right text-[11px] font-bold text-gray-500 uppercase whitespace-nowrap">Fərq</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {yeniSayimResult.map((row, i) => {
                                            const fmt       = v => formatByUnit(v, row.unit);
                                            const ferqColor = row.ferq === null ? '' : row.ferq > 0 ? 'text-green-600' : row.ferq < 0 ? 'text-red-600' : 'text-gray-500';
                                            return (
                                                <tr key={row.id} className={`border-t border-gray-50 ${i % 2 === 1 ? 'bg-gray-50/40' : ''}`}>
                                                    <td className="px-3 py-2.5 font-semibold text-sm text-gray-800">{row.name}</td>
                                                    <td className="px-3 py-2.5 text-right font-mono text-sm text-gray-600">{fmt(row.kecan_sayim)}</td>
                                                    <td className="px-3 py-2.5 text-right font-mono text-sm text-green-600">
                                                        {row.medaxil > 0 ? fmt(row.medaxil) : <span className="text-gray-300">0</span>}
                                                    </td>
                                                    <td className="px-3 py-2.5 text-right font-mono text-sm text-orange-500">
                                                        {row.medaxil_itki > 0 ? fmt(row.medaxil_itki) : <span className="text-gray-300">0</span>}
                                                        {medItkiRate(row.unit, row.medaxil_itki_percent) && (
                                                            <span className="block text-[10px] font-sans text-gray-400">{medItkiRate(row.unit, row.medaxil_itki_percent)}</span>
                                                        )}
                                                    </td>
                                                    <td className="px-3 py-2.5 text-right font-mono text-sm text-gray-500">
                                                        {row.istifade > 0 ? fmt(row.istifade) : <span className="text-gray-300">0</span>}
                                                    </td>
                                                    <td className="px-3 py-2.5 text-right font-mono text-sm text-red-500">
                                                        {row.itki > 0 ? fmt(row.itki) : <span className="text-gray-300">0</span>}
                                                    </td>
                                                    <td className="px-3 py-2.5 text-right font-mono text-sm text-amber-600">
                                                        {row.terg > 0 ? fmt(row.terg) : <span className="text-gray-300">0</span>}
                                                    </td>
                                                    <td className="px-3 py-2.5 text-right font-mono text-sm text-purple-600">
                                                        {row.prefaktoriya > 0 ? fmt(row.prefaktoriya) : <span className="text-gray-300">0</span>}
                                                    </td>
                                                    <td className="px-3 py-2.5 text-right font-mono font-bold text-sm text-primary-700">
                                                        <span className="px-2 py-0.5 rounded-lg bg-primary-50">{fmt(row.gozlenilen)}</span>
                                                    </td>
                                                    <td className="px-3 py-2.5 text-right font-mono font-bold text-sm text-indigo-600">
                                                        {row.hasSayilan ? fmt(row.sayilan) : <span className="text-gray-300">—</span>}
                                                    </td>
                                                    <td className={`px-3 py-2.5 text-right font-mono font-bold text-sm ${ferqColor}`}>
                                                        {row.ferq !== null
                                                            ? <span className={`px-2 py-0.5 rounded-lg ${row.ferq > 0 ? 'bg-green-50' : row.ferq < 0 ? 'bg-red-50' : 'bg-gray-50'}`}>
                                                                {row.ferq > 0 ? '+' : ''}{fmt(row.ferq)}
                                                              </span>
                                                            : <span className="text-gray-300">—</span>}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                            <div className="px-6 py-4 border-t border-gray-100 flex-shrink-0 flex items-center justify-between">
                                <div className="text-xs text-gray-400 flex gap-3">
                                    <span>{yeniSayimResult.filter(r => r.hasSayilan).length} sayıldı</span>
                                    <span className="text-red-500 font-semibold">
                                        {yeniSayimResult.filter(r => r.ferq !== null && r.ferq < 0).length} kəsir
                                    </span>
                                    <span className="text-green-600 font-semibold">
                                        {yeniSayimResult.filter(r => r.ferq !== null && r.ferq > 0).length} artıq
                                    </span>
                                </div>
                                <div className="flex gap-3">
                                    <button onClick={() => setYeniSayimResult(null)}
                                        className="px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-500 hover:bg-gray-50">
                                        ← Geri
                                    </button>
                                    <button onClick={() => openPrintSelect(yeniSayimResult)}
                                        className="px-4 py-2.5 rounded-xl text-sm font-bold text-white bg-slate-800 hover:bg-slate-700">
                                        🖨️ Çap et
                                    </button>
                                    <button onClick={() => { setYeniSayimModal(false); }}
                                        className="px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-500 hover:bg-gray-50">
                                        Bağla
                                    </button>
                                </div>
                            </div>
                        </>)}
                    </div>
                </div>
            )}

            {/* ── DEPO İDARƏETMƏ MODAL ── */}
            {depotModal && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
                     onClick={e => e.target === e.currentTarget && setDepotModal(false)}>
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
                        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                            <h3 className="font-bold text-gray-800">🏪 Depo / Bölmələr</h3>
                            <button onClick={() => setDepotModal(false)} className="text-gray-400 hover:text-gray-600 text-xl leading-none">✕</button>
                        </div>
                        {/* Mövcud depolar */}
                        <div className="px-5 py-3 max-h-48 overflow-y-auto">
                            {depots.length === 0 ? (
                                <p className="text-sm text-gray-400 text-center py-4">Hələ depo yoxdur.</p>
                            ) : depots.map(d => (
                                <div key={d.id} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                                    {editDepot?.id === d.id ? (
                                        <div className="flex gap-2 flex-1">
                                            <input
                                                type="text"
                                                value={depotForm.name}
                                                onChange={e => setDepotForm({ name: e.target.value })}
                                                className="flex-1 px-2 py-1 border border-primary-300 rounded-lg text-sm focus:outline-none"
                                                autoFocus
                                            />
                                            <button onClick={handleDepotSubmit} disabled={depotLoading}
                                                className="px-3 py-1 rounded-lg text-xs font-bold text-white bg-primary-500 hover:bg-primary-600">
                                                {depotLoading ? '...' : '✓'}
                                            </button>
                                            <button onClick={() => { setEditDepot(null); setDepotForm({ name: '' }); }}
                                                className="px-2 py-1 rounded-lg text-xs text-gray-500 border border-gray-200">✕</button>
                                        </div>
                                    ) : (
                                        <>
                                            <span className="text-sm font-semibold text-gray-800">{d.name}</span>
                                            <div className="flex gap-1.5">
                                                <button onClick={() => { setEditDepot(d); setDepotForm({ name: d.name }); }}
                                                    className="px-2 py-1 rounded-lg text-[11px] text-gray-500 border border-gray-200 hover:bg-gray-50">✏️</button>
                                                <button onClick={() => handleDepotDelete(d)}
                                                    className="px-2 py-1 rounded-lg text-[11px] text-red-500 border border-red-100 hover:bg-red-50">🗑️</button>
                                            </div>
                                        </>
                                    )}
                                </div>
                            ))}
                        </div>
                        {/* Yeni depo */}
                        {!editDepot && (
                            <form onSubmit={handleDepotSubmit} className="px-5 py-4 border-t border-gray-100 flex gap-2">
                                <input
                                    type="text"
                                    value={depotForm.name}
                                    onChange={e => setDepotForm({ name: e.target.value })}
                                    placeholder="Yeni depo adı... (Bar, İsti mətbəx...)"
                                    className="flex-1 px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary-400"
                                    required
                                />
                                <button type="submit" disabled={depotLoading}
                                    className="px-4 py-2 rounded-xl text-sm font-bold text-white bg-primary-500 hover:bg-primary-600 disabled:opacity-50">
                                    {depotLoading ? '...' : '+ Əlavə et'}
                                </button>
                            </form>
                        )}
                    </div>
                </div>
            )}

            {/* ── TEXKARTA ÇAP MODAL ── */}
            {recipeModal && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
                     onClick={(e) => e.target === e.currentTarget && setRecipeModal(false)}>
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
                        <h3 className="text-lg font-bold text-gray-800 mb-2">📋 Texkarta çapı</h3>
                        <p className="text-sm text-gray-500 mb-5">Hansı formatda çap olunsun?</p>

                        <div className="flex flex-col gap-3">
                            <button
                                onClick={() => { handleRecipePrint('empty'); setRecipeModal(false); }}
                                className="flex items-center gap-3 p-4 rounded-xl border-2 border-amber-200 bg-amber-50 hover:bg-amber-100 text-left transition-all"
                            >
                                <span className="text-3xl">✏️</span>
                                <div className="flex-1">
                                    <div className="font-bold text-amber-800">Boş çap et</div>
                                    <div className="text-xs text-amber-700">Yalnız menyu adları, qarşısı boş — qələmlə doldurmaq üçün</div>
                                </div>
                            </button>

                            <button
                                onClick={() => { handleRecipePrint('full'); setRecipeModal(false); }}
                                className="flex items-center gap-3 p-4 rounded-xl border-2 border-emerald-200 bg-emerald-50 hover:bg-emerald-100 text-left transition-all"
                            >
                                <span className="text-3xl">📝</span>
                                <div className="flex-1">
                                    <div className="font-bold text-emerald-800">Dolu çap et</div>
                                    <div className="text-xs text-emerald-700">Bütün inqredientlər miqdar və ölçü vahidi ilə birlikdə</div>
                                </div>
                            </button>
                        </div>

                        <button
                            onClick={() => setRecipeModal(false)}
                            className="w-full mt-4 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-500 hover:bg-gray-50"
                        >
                            Ləğv et
                        </button>
                    </div>
                </div>
            )}

            {/* ── PRINTER SEÇİM MODAL ── */}
            {printSelectModal && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
                     onClick={e => e.target === e.currentTarget && setPrintSelectModal(false)}>
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm">
                        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                            <h3 className="font-bold text-gray-800">🖨️ Printer seç</h3>
                            <button onClick={() => setPrintSelectModal(false)} className="text-gray-400 hover:text-gray-600 text-xl leading-none">✕</button>
                        </div>
                        <div className="p-5 flex flex-col gap-3">
                            {printers.length === 0 ? (
                                <p className="text-sm text-gray-400 text-center">Printer tapılmadı. Ayarlardan əlavə edin.</p>
                            ) : (
                                <div className="flex flex-col gap-2">
                                    {printers.map(p => (
                                        <button
                                            key={p.id}
                                            onClick={() => setSelectedPrinter(p.name)}
                                            className={`w-full text-left px-4 py-3 rounded-xl border-2 transition-all ${
                                                selectedPrinter === p.name
                                                    ? 'border-primary-500 bg-primary-50 text-primary-700'
                                                    : 'border-gray-200 hover:border-gray-300'
                                            }`}
                                        >
                                            <p className="font-semibold text-sm">{p.label || p.name}</p>
                                            <p className="text-xs text-gray-400 font-mono mt-0.5">{p.name}</p>
                                        </button>
                                    ))}
                                </div>
                            )}
                            <div className="flex gap-3 mt-1">
                                <button onClick={() => setPrintSelectModal(false)}
                                    className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-500">
                                    Ləğv et
                                </button>
                                <button
                                    onClick={() => {
                                        setPrintSelectModal(false);
                                        // Blank sheet (sayım vərəqi) seçildisə birbaşa WebView2 bridge-ə göndər
                                        if (pendingPrintData?.__blankSheet) {
                                            doPrintHtml(pendingPrintData.html, selectedPrinter);
                                            toast.success(`Sayım vərəqi ${selectedPrinter}-ə göndərildi.`);
                                        } else {
                                            handleSayimPrint(pendingPrintData, selectedPrinter || null);
                                        }
                                    }}
                                    className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white bg-slate-800 hover:bg-slate-700">
                                    🖨️ Çap et
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* VARİANT SEÇİM MODAL */}
            {waModal && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
                     onClick={e => e.target === e.currentTarget && setWaModal(false)}>
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm">
                        <div className="px-5 py-4 border-b border-gray-100">
                            <h3 className="font-bold text-gray-800">📱 WhatsApp-a göndər</h3>
                            <p className="text-xs text-gray-400 mt-0.5">
                                {waMode === 'ingredient'
                                    ? `«${waIng?.name}» — hansı məhsullarda nə qədər`
                                    : `${selectedItem?.name}${selectedVariant ? ` — ${selectedVariant.name}` : ''} texkartası`}
                            </p>
                        </div>
                        <div className="p-5 space-y-3">
                            {/* 1 input — nömrə yaz */}
                            <div>
                                <label className="text-[11px] font-semibold text-gray-500">Nömrə</label>
                                <input
                                    type="tel"
                                    value={waNum}
                                    onChange={e => setWaNum(e.target.value)}
                                    placeholder="Nömrə daxil edin"
                                    autoFocus
                                    className="mt-1 w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm font-mono focus:outline-none focus:border-green-400"
                                />
                            </div>

                            {/* Alış qiymətləri ilə göndər — tick (yalnız məhsul texkartası rejimində) */}
                            {waMode !== 'ingredient' && (
                            <label className="flex cursor-pointer items-center gap-2 rounded-xl border border-amber-100 bg-amber-50/50 px-3 py-2.5 text-sm font-semibold text-amber-800">
                                <input type="checkbox" checked={waWithPrices}
                                    onChange={e => setWaWithPrices(e.target.checked)}
                                    className="h-4 w-4 rounded border-gray-300 text-amber-600" />
                                💰 Alış qiymətləri ilə göndər
                            </label>
                            )}

                            {/* Son göndərilmiş nömrələr — tıklayanda input-u doldurur */}
                            {waHistory.length > 0 && (
                                <div>
                                    <p className="text-[11px] font-semibold text-gray-500 mb-1.5">Son göndərilənlər</p>
                                    <div className="flex flex-wrap gap-2">
                                        {waHistory.map((n) => (
                                            <button
                                                key={n}
                                                type="button"
                                                onClick={() => setWaNum(n)}
                                                className={`px-3 py-1.5 rounded-full text-xs font-mono font-semibold border transition-all ${
                                                    waNum === n
                                                        ? 'bg-green-600 text-white border-green-600'
                                                        : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-green-50 hover:border-green-300'
                                                }`}
                                            >
                                                📲 {n}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Şəkil önizləmə (varsa — şəkillə göndəriləcək) */}
                            {waMode !== 'ingredient' && selectedItem?.image_url && (
                                <div className="flex items-center gap-2 rounded-lg bg-green-50 border border-green-100 px-2.5 py-2">
                                    <img src={`${getAssetBaseUrl()}${selectedItem.image_url}`} alt=""
                                        className="h-10 w-10 rounded object-cover border border-green-200" />
                                    <span className="text-[11px] text-green-700">Şəkillə göndəriləcək (caption = texkarta)</span>
                                </div>
                            )}

                            {/* Mesaj önizləmə */}
                            <div className="rounded-lg bg-gray-50 border border-gray-100 p-2.5 text-[11px] text-gray-500 whitespace-pre-wrap max-h-32 overflow-y-auto font-mono">
                                {waMode === 'ingredient' ? buildIngredientUsageMessage() : buildTexkartaMessage()}
                            </div>
                        </div>
                        <div className="px-5 py-4 border-t border-gray-100 flex gap-2">
                            <button onClick={() => setWaModal(false)}
                                className="flex-1 px-3 py-2 rounded-lg text-sm font-bold text-gray-500 border border-gray-200 hover:bg-gray-50">
                                Bağla
                            </button>
                            <button onClick={sendTexkartaWhatsapp} disabled={waSending}
                                className="flex-1 px-3 py-2 rounded-lg text-sm font-bold text-white bg-green-600 hover:bg-green-700 disabled:opacity-50">
                                {waSending ? 'Göndərilir...' : '📤 Göndər'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {variantPickModal && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
                     onClick={e => e.target === e.currentTarget && setVariantPickModal(null)}>
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm">
                        <div className="px-5 py-4 border-b border-gray-100">
                            <h3 className="font-bold text-gray-800">🔀 Variant seçin</h3>
                            <p className="text-xs text-gray-400 mt-0.5">{variantPickModal.item.name} — hansı variant üçün texkarta yazırsınız?</p>
                        </div>
                        <div className="p-3 flex flex-col gap-1.5">
                            {/* Base / no variant */}
                            <button
                                type="button"
                                onClick={() => handleSelectVariant(variantPickModal.item, null)}
                                className="w-full text-left px-4 py-3 rounded-xl border border-gray-200 hover:bg-primary-50 hover:border-primary-300 transition-all"
                            >
                                <p className="font-semibold text-sm text-gray-800">🍽️ Ortaq texkarta (bütün variantlar)</p>
                                <p className="text-xs text-gray-400">Variantda ayrıca yazılmayıbsa hər yerdə (stok, maya, mətbəx) buradan götürülür</p>
                            </button>
                            {variantPickModal.variants.map(v => (
                                <button
                                    key={v.id}
                                    type="button"
                                    onClick={() => handleSelectVariant(variantPickModal.item, v)}
                                    className="w-full text-left px-4 py-3 rounded-xl border border-gray-200 hover:bg-primary-50 hover:border-primary-300 transition-all"
                                >
                                    <div className="flex items-center justify-between">
                                        <p className="font-semibold text-sm text-gray-800">{v.name}</p>
                                        <span className="text-sm font-bold text-primary-600">{trimNumber(v.price)} ₼</span>
                                    </div>
                                    {v.printer && <p className="text-[11px] text-gray-400 mt-0.5">🖨️ {v.printer}</p>}
                                </button>
                            ))}
                        </div>
                        <div className="px-5 pb-4 pt-1">
                            <button type="button" onClick={() => setVariantPickModal(null)}
                                className="w-full py-2 rounded-xl border border-gray-200 text-sm text-gray-500 font-semibold hover:bg-gray-50">
                                Ləğv et
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* STOK MODAL */}
            {stockModal && selectedIng && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
                     onClick={e => e.target === e.currentTarget && setStockModal(false)}>
                    <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl">
                        <h3 className="font-bold text-lg mb-1 text-gray-800">📦 Stok yenilə</h3>
                        <p className="text-sm text-gray-400 mb-5">
                            {selectedIng.name} — Cari: {formatByUnit(selectedIng.stock, selectedIng.unit)}
                        </p>
                        <form onSubmit={handleStockSubmit} className="flex flex-col gap-4">
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1.5">Əməliyyat növü</label>
                                <div className="grid grid-cols-3 gap-2">
                                    {[
                                        { key: 'giris',    label: '📥 Giriş',    color: 'border-green-400 bg-green-50 text-green-700' },
                                        { key: 'cixis',    label: '📤 Çıxış',    color: 'border-red-400 bg-red-50 text-red-600'       },
                                        { key: 'duzeltme', label: '✏️ Düzəlt',   color: 'border-blue-400 bg-blue-50 text-blue-600'    },
                                    ].map(t => (
                                        <button
                                            key={t.key}
                                            type="button"
                                            onClick={() => setStockForm({ ...stockForm, type: t.key })}
                                            className={`py-2.5 rounded-xl border-2 text-xs font-bold transition-all ${
                                                stockForm.type === t.key
                                                    ? t.color
                                                    : 'border-gray-200 text-gray-500'
                                            }`}
                                        >
                                            {t.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1.5">
                                    {stockForm.type === 'duzeltme' ? 'Yeni miqdar' : 'Miqdar'} ({unitLabel(selectedIng.unit)})
                                </label>
                                <input
                                    type="number"
                                    value={stockForm.quantity}
                                    onChange={e => setStockForm({ ...stockForm, quantity: e.target.value })}
                                    step={selectedIng.unit === 'eded' ? '1' : '0.001'} min="0"
                                    className={inputClass}
                                    autoFocus
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1.5">Qeyd</label>
                                <input
                                    type="text"
                                    value={stockForm.note}
                                    onChange={e => setStockForm({ ...stockForm, note: e.target.value })}
                                    placeholder="İstəyə bağlı..."
                                    className={inputClass}
                                />
                            </div>
                            <div className="flex gap-3 mt-2">
                                <button type="button" onClick={() => setStockModal(false)}
                                    className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-500">
                                    Ləğv et
                                </button>
                                <button type="submit"
                                    className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white"
                                    style={{ background: 'linear-gradient(135deg, #006f91, #0090bb)' }}>
                                    Yadda saxla
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* İNQREDİENT MODAL */}
            {ingModal && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
                     onClick={e => e.target === e.currentTarget && setIngModal(false)}>
                    <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl">
                        <h3 className="font-bold text-lg mb-5 text-gray-800">
                            {editIng ? 'İnqredienti yenilə' : 'Yeni inqredient'}
                        </h3>
                        <form onSubmit={handleIngSubmit} className="flex flex-col gap-4">
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1.5">Ad</label>
                                <input
                                    type="text"
                                    value={ingForm.name}
                                    onChange={e => setIngForm({ ...ingForm, name: e.target.value })}
                                    placeholder="Məs: Yumurta"
                                    className={inputClass}
                                    autoFocus
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1.5">Ölçü vahidi</label>
                                <SearchSelect
                                    value={ingForm.unit}
                                    onChange={v => setIngForm({ ...ingForm, unit: v })}
                                    options={[
                                        { value: 'gram', label: 'Gram (q)' },
                                        { value: 'ml',   label: 'Millilitr (ml)' },
                                        { value: 'eded', label: 'Ədəd (əd)' },
                                        { value: 'kg',   label: 'Kiloqram (kq)' },
                                        { value: 'litr', label: 'Litr (lt)' },
                                    ]}
                                    placeholder="Vahid seç..."
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 uppercase mb-1.5">Depo / Bölmə</label>
                                <SearchSelect
                                    value={ingForm.depot_id}
                                    onChange={v => setIngForm({ ...ingForm, depot_id: v })}
                                    options={[
                                        { value: '', label: '— Depo seçilməyib —' },
                                        ...depots.map(d => ({ value: String(d.id), label: d.name }))
                                    ]}
                                    placeholder="Depo seç..."
                                />
                            </div>
                            {!editIng && (
                                <div>
                                    <label className="block text-xs font-semibold text-gray-500 uppercase mb-1.5">İlk stok miqdarı</label>
                                    <input
                                        type="number"
                                        value={ingForm.stock}
                                        onChange={e => setIngForm({ ...ingForm, stock: e.target.value })}
                                        step={ingredientStep} min="0"
                                        className={inputClass}
                                    />
                                </div>
                            )}
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs font-semibold text-gray-500 uppercase mb-1.5">Min stok</label>
                                    <input
                                        type="number"
                                        value={ingForm.min_stock}
                                        onChange={e => setIngForm({ ...ingForm, min_stock: e.target.value })}
                                        step={ingredientStep} min="0"
                                        className={inputClass}
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-gray-500 uppercase mb-1.5">Vahid qiymət (₼)</label>
                                    <input
                                        type="number"
                                        value={ingForm.cost_per_unit}
                                        onChange={e => setIngForm({ ...ingForm, cost_per_unit: e.target.value })}
                                        step="0.0001" min="0"
                                        className={inputClass}
                                    />
                                </div>
                            </div>
                            {(() => {
                                const isSubUnit = ingForm.unit === 'gram' || ingForm.unit === 'ml';
                                const perLabel  = isSubUnit ? '1kq/lt-da' : '1 vahiddə';
                                const unitSym   = ingForm.unit === 'gram' ? 'q' : ingForm.unit === 'ml' ? 'ml' : ingForm.unit === 'kg' ? 'kq' : ingForm.unit === 'litr' ? 'lt' : 'əd';
                                return (<>
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-500 uppercase mb-1.5">
                                            Mexaric itkisi
                                            <span className="ml-1 font-normal text-gray-400 normal-case">({perLabel} nə qədər {unitSym} itirir — bişirmə)</span>
                                        </label>
                                        <input
                                            type="number"
                                            value={ingForm.mexaric_itki_percent}
                                            onChange={e => setIngForm({ ...ingForm, mexaric_itki_percent: e.target.value })}
                                            step="0.001" min="0"
                                            placeholder={isSubUnit ? 'Məs: 350 (350q/kq)' : 'Məs: 0.35'}
                                            className={inputClass}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-500 uppercase mb-1.5">
                                            Mədaxil itkisi
                                            <span className="ml-1 font-normal text-gray-400 normal-case">({perLabel} nə qədər {unitSym} itirir — qəbul)</span>
                                        </label>
                                        <input
                                            type="number"
                                            value={ingForm.medaxil_itki_percent}
                                            onChange={e => setIngForm({ ...ingForm, medaxil_itki_percent: e.target.value })}
                                            step="0.001" min="0"
                                            placeholder={isSubUnit ? 'Məs: 100 (100q/kq)' : 'Məs: 0.1'}
                                            className={inputClass}
                                        />
                                    </div>
                                </>);
                            })()}
                            <div className="flex gap-3 mt-2">
                                <button type="button" onClick={() => { recipeAutoSelectRef.current = false; setIngModal(false); }}
                                    className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-500">
                                    Ləğv et
                                </button>
                                <button type="submit"
                                    className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white"
                                    style={{ background: 'linear-gradient(135deg, #006f91, #0090bb)' }}>
                                    {editIng ? 'Yadda saxla' : 'Əlavə et'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {deleteIngredient && (
                <div
                    className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
                    onClick={e => e.target === e.currentTarget && setDeleteIngredient(null)}
                >
                    <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl">
                        <h3 className="font-bold text-lg mb-2 text-gray-800">İnqredienti sil</h3>
                        <p className="text-sm text-gray-500 mb-5">
                            <span className="font-semibold text-gray-700">{deleteIngredient.name}</span> silinsin?
                        </p>
                        <div className="flex gap-3">
                            <button
                                type="button"
                                onClick={() => setDeleteIngredient(null)}
                                className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-500"
                            >
                                Geri
                            </button>
                            <button
                                type="button"
                                onClick={handleIngDelete}
                                className="flex-1 py-2.5 rounded-xl bg-red-500 text-sm font-bold text-white hover:bg-red-600"
                            >
                                Sil
                            </button>
                        </div>
                    </div>
                </div>
            )}
            <LossCalcModal open={lossCalcOpen} onClose={() => setLossCalcOpen(false)} />
        </Layout>
    );
};

export default WarehouseDashboard;
